import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiFileText, FiPrinter, FiDollarSign, FiLink, FiPackage
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import styled from 'styled-components';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import LinkedDocNumbers, { parentLinks } from '../../components/LinkedDocNumbers';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { confirmZeroPriceLines } from '../../utils/zeroPriceLines';
import { formatAmount } from '../../utils/documentNumbering';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import LetterheadPicker from '../../components/LetterheadPicker';
import { INVOICE_LETTERHEAD_MODULE, listInvoiceLetterheads, letterheadForChoice } from '../../utils/invoiceLetterheads';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, getTransaction, updateTransactionStatus
} from '../../utils/transactionHelper';
import { deleteSubTransactions } from '../../utils/subTransactionHelper';
import { deleteStockFlow, getAvailableBatches, getAvailableSerials, getItemStockCached, invalidateItemStock, getRecentItemPrices } from '../../utils/stockFlowHelper';
import { getCustomerBalance } from '../../utils/liveBalanceHelper';
import ItemPriceHints from '../../components/ItemPriceHints';
import PageLayout from '../../components/PageLayout';
import CustomerSelect from '../../components/CustomerSelect';
import ItemSelect from '../../components/ItemSelect';
import QuickAddItemModal from '../../components/QuickAddItemModal';
import SerialSelectPopover from '../../components/SerialSelectPopover';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import { getItemsCached, getCustomersCached, getAccountsCached, getEmployeesCached, getJobsCached, getLocationsCached, getAreasRegionsCached } from '../../utils/masterDataStore';
import useTableSort from '../../hooks/useTableSort';
import AttachmentPanel from '../../components/AttachmentPanel';
import {
  checkStockAvailability, checkCustomerCreditLimit, isFutureDate,
  isLargeTransaction, LARGE_TRANSACTION_THRESHOLD, writeAuditLog, toBaseQty
} from '../../utils/validationHelpers';
import { loadIfEnabled as loadCustomerPricing, EMPTY_PRICING, filterItemsByPricing, getOverridePrice } from '../../utils/customerPricingHelper';
import { subCategoryLineDiscount } from '../../utils/customerSubCategoryDiscount';
import { recomputeSizeLine } from '../../utils/sizeHelper';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import { sendDocumentViaWhatsApp } from '../../utils/whatsappHelper';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../config/firebase';
import { doc, getDoc, getDocs, updateDoc, collection, query, where } from 'firebase/firestore';
import QRCode from 'qrcode';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, BalanceRow, DiscountInput,
  PaymentSection, LinkedBadgeContainer, LinkedDocsSection, SortableTh, SortContext
} from '../../components/SharedStyles';
import SizeLineRow from '../../components/SizeLineRow';
import { startSaveTimer } from '../../utils/saveTimer';
import { prewarmSave } from '../../utils/transactionHelper';
import { percentForDiscountAmount } from '../../utils/discountPercent';
import TwoUnitQtyBox, { TwoUnitNote } from '../../components/TwoUnitQtyBox';
import { twoUnitSwitchQty } from '../../utils/unitConversion';
import { inActiveBranch } from '../../utils/branchLink';
import { carryEmployeeJob, hsCodePatch, customerPOOf, joinCustomerPOs } from '../../utils/linkCarry';

const TRAN_TYPE_CODE = 'SI';

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Posted', 'Paid'],
  outstandingStatuses: ['Posted', 'Partial'],
  outstandingField: 'balanceAmount'
};

const EMPTY_LINE = {
  itemId: '',
  accountId: '',
  description: '',
  quantity: 1,
  unitOfMeasure: '',
  unitCode: '',
  unitPrice: 0,
  unitCost: 0, // Cost price for COGS calculation
  priceType: 'Retail',
  discountPercent: 0,
  discountAmount: 0,
  taxable: false,
  taxRate: 0,
  taxAmount: 0,
  thirdSchedule: false, // module sales_third_schedule_tax: GST on gross (before discount) when true
  mrp: 0,
  lineTotal: 0,
  totalCost: 0, // quantity * unitCost for COGS entries
  trackingType: 'None',
  batchNumber: '',
  serialNumbers: [],
  hsCode: '',
  sourceDocId: '',
  sourceDocNumber: '',
  sourceDocDate: '',
  sourceLineNumber: 0
};

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.info.main,
  Partial: '#9C27B0',
  Paid: colors.success.main,
  Cancelled: colors.error.main,
};

// ── Sale Order delivery progress (FLW-03) ───────────────────────────────────
// An SO is delivered by its active Delivery Challans (not Draft / Cancelled) and
// by posted invoices made straight from it. Each invoice line made from an SO
// keeps the SO line it came from (soLineNumber = that line's number on the SO);
// older lines without it fall back to the same item (or description).
const DC_ACTIVE = (s) => !!s && s !== 'Draft' && s !== 'Cancelled';
const INVOICE_LIVE = ['Posted', 'Partial', 'Paid'];
// 'Delivered' is an old, invalid SO status — it is corrected on the next recalc.
const SO_PROGRESS_STATUSES = ['Approved', 'Partially Delivered', 'Delivered', 'Completed'];
const r6 = (n) => Math.round((Number(n) || 0) * 1e6) / 1e6;
const soLineNo = (sl, idx) => Number(sl?.lineNumber) || (idx + 1);

const findSOLineIndex = (soLines, delivered, ref, itemId, desc, qty, removing) => {
  const n = Number(ref) || 0;
  if (n > 0) {
    const i = soLines.findIndex((sl, idx) => soLineNo(sl, idx) === n);
    if (i >= 0 && (!itemId || !soLines[i].itemId || soLines[i].itemId === itemId)) return i;
  }
  const same = [];
  soLines.forEach((sl, idx) => {
    if (itemId ? sl.itemId === itemId : (!!desc && !sl.itemId && sl.description === desc)) same.push(idx);
  });
  const fit = same.find(idx => (removing
    ? (delivered[idx] || 0) >= qty
    : (Number(soLines[idx].quantity) || 0) - (delivered[idx] || 0) > 0));
  return fit !== undefined ? fit : (same.length ? same[0] : -1);
};

const countsOnSO = (d) => (d.tranTypeCode === 'DC'
  ? DC_ACTIVE(d.status)
  : (['SI', 'NSI'].includes(d.tranTypeCode) && INVOICE_LIVE.includes(d.status)));

// Delivered qty per SO line from the SO's child documents (skipId = leave one out).
// Lines that carry their SO line number are applied first, older lines without
// it then fill whatever is still open on a line of the same item.
export const soDeliveredFromChildren = (soLines, children, skipId) => {
  const delivered = (soLines || []).map(() => 0);
  const live = (children || []).filter(c => c && c.id !== skipId && countsOnSO(c));
  const add = (l) => {
    const qty = Number(l.quantity) || 0;
    if (!(qty > 0)) return;
    const i = findSOLineIndex(soLines, delivered, l.soLineNumber, l.itemId || '', l.description || '', qty, false);
    if (i >= 0) delivered[i] = r6(delivered[i] + qty);
  };
  live.forEach(c => (c.lines || []).forEach(l => { if (Number(l.soLineNumber) > 0) add(l); }));
  live.forEach(c => (c.lines || []).forEach(l => { if (!(Number(l.soLineNumber) > 0)) add(l); }));
  return delivered;
};

// SO line number each document line belongs to (tags older lines saved without
// it). `progress` (from loadSOProgress) = what OTHER documents already delivered,
// so an old line goes to the SO line that still has room for it.
export const tagSOLineNumbers = (soLines, docLines, progress = null) => {
  const used = (soLines || []).map((sl, i) => {
    const p = progress && progress[soLineNo(sl, i)];
    return p ? (Number(p.delivered) || 0) : 0;
  });
  return (docLines || []).map(l => {
    const qty = Number(l.quantity) || 0;
    const i = findSOLineIndex(soLines, used, l.soLineNumber, l.itemId || '', l.description || '', qty, false);
    if (i < 0) return 0;
    used[i] = r6(used[i] + qty);
    return soLineNo(soLines[i], i);
  });
};

// Approved (nothing delivered) / Partially Delivered / Completed (all delivered).
// A Draft or Cancelled SO keeps its status.
export const soStatusFromLines = (lines, current) => {
  if (!SO_PROGRESS_STATUSES.includes(current)) return current;
  const all = lines.length > 0 && lines.every(l => (Number(l.deliveredQty) || 0) >= (Number(l.quantity) || 0));
  const some = lines.some(l => (Number(l.deliveredQty) || 0) > 0);
  return all ? 'Completed' : some ? 'Partially Delivered' : 'Approved';
};

const listSOChildren = async (companyId, soId) => {
  const snap = await getDocs(query(collection(db, 'companies', companyId, 'transactions'), where('parentId', '==', soId)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Per SO line (keyed by its line number): ordered, delivered by OTHER documents,
// and a label. Always counted from the SO's linked documents (one query), so a
// list of orders loaded earlier on this screen can never make it stale.
export const loadSOProgress = async (companyId, so, self) => {
  const soLines = so.lines || [];
  const delivered = soDeliveredFromChildren(soLines, await listSOChildren(companyId, so.id), self ? self.id : null);
  const progress = {};
  soLines.forEach((l, i) => {
    progress[soLineNo(l, i)] = { ordered: Number(l.quantity) || 0, delivered: delivered[i], label: l.description || l.itemName || `Line ${i + 1}` };
  });
  return progress;
};

// Message when a line asks for more than is left on the SO ('' when fine).
export const soOverDeliveryMessage = (lines, progress, soNumber) => {
  if (!progress) return '';
  const want = {};
  (lines || []).forEach(l => {
    const n = Number(l.soLineNumber) || 0;
    if (n > 0 && progress[n]) want[n] = r6((want[n] || 0) + (Number(l.quantity) || 0));
  });
  const over = Object.keys(want).filter(n => want[n] > r6(progress[n].ordered - progress[n].delivered) + 1e-6);
  if (over.length === 0) return '';
  return `Quantity is more than what is left on Sale Order ${soNumber || ''}:\n\n` + over.map(n => {
    const p = progress[n];
    return `• ${p.label}: ordered ${p.ordered}, already delivered ${p.delivered}, remaining ${Math.max(0, r6(p.ordered - p.delivered))} — you entered ${want[n]}`;
  }).join('\n') + '\n\nPlease reduce the quantity to the remaining amount.';
};

// Recount the SO's per-line delivered / pending quantities from all its linked
// documents (active challans + posted direct invoices) and set a valid status.
// Writes only lines / status / updatedAt / updatedBy (the SO progress fields an
// invoice user may change). Called after the invoice itself was saved / deleted.
export const recalcSOFromChildren = async (companyId, soId, userId) => {
  const ref = doc(db, 'companies', companyId, 'transactions', soId);
  const snap = await getDoc(ref);
  if (!snap.exists() || snap.data().tranTypeCode !== 'SO') return null;
  const so = snap.data();
  const soLines = Array.isArray(so.lines) ? so.lines : [];
  const delivered = soDeliveredFromChildren(soLines, await listSOChildren(companyId, soId), null);
  const lines = soLines.map((l, i) => ({
    ...l, deliveredQty: delivered[i], pendingQty: Math.max(0, r6((Number(l.quantity) || 0) - delivered[i]))
  }));
  const upd = { lines, updatedAt: new Date().toISOString(), ...(userId ? { updatedBy: userId } : {}) };
  const status = soStatusFromLines(lines, so.status);
  if (status !== so.status) upd.status = status;
  await updateDoc(ref, upd);
  return { ...so, ...upd };
};

// Save / edit / cancel / delete of an invoice: recount the SO(s) it was or is
// linked to.
export const syncInvoiceSOProgress = async (companyId, oldInv, newInv, userId) => {
  const ids = [];
  [oldInv, newInv].forEach(inv => {
    if (inv && inv.parentTranType === 'SO' && inv.parentId && !ids.includes(inv.parentId)) ids.push(inv.parentId);
  });
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    await recalcSOFromChildren(companyId, id, userId);
  }
};

// LinkedBadge component for document linking display
// Shows the linked documents' NUMBERS (not a count). `known` = numbers the
// document already carries (no lookup); the rest are looked up once per device.
const LinkedBadge = ({ label, ids, known, max = 3, icon: Icon = FiLink }) => {
  const list = (ids || []).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <LinkedBadgeContainer>
      <Icon size={12} />
      <span>{label}:</span>
      <LinkedDocNumbers ids={list} known={known} max={max} showIcon={false} color="inherit" style={{ fontSize: 'inherit' }} />
    </LinkedBadgeContainer>
  );
};

const SaleInvoice = () => {
  const location = useLocation();
  const { activeCompany, activeFinancialYear, currentUser, activeBranch, isModuleEnabled } = useAuth();
  // Speed: prepare the first save in the background as soon as this screen opens.
  useEffect(() => {
    if (!activeCompany?.id || !activeFinancialYear?.id) return;
    prewarmSave(activeCompany.id, {
      tranTypeCode: TRAN_TYPE_CODE,
      branchId: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null,
      financialYearId: activeFinancialYear.id,
    });
  }, [activeCompany?.id, activeFinancialYear?.id, activeBranch?.id]);
  const multiUnitEnabled = isModuleEnabled && isModuleEnabled('master_units');
  const mrpEnabled = isModuleEnabled && isModuleEnabled('mrp_pricing');
  const priceClassEnabled = isModuleEnabled && isModuleEnabled('customer_class');
  const thirdScheduleEnabled = isModuleEnabled && isModuleEnabled('sales_third_schedule_tax');
  // WHT display label + direction (company settings). Blank label → 'WHT'.
  const whtLabel = (activeCompany?.whtLabel || '').trim() || 'WHT';
  const whtAddToTotal = activeCompany?.whtAddToTotal === true;
  const { canEdit, canDelete, canPost, canCancel, canChange } = usePagePermissions('transactions', 'saleInvoice');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [printConfig, setPrintConfig] = useState(null);
  const letterheadChoiceOn = !!(isModuleEnabled && isModuleEnabled(INVOICE_LETTERHEAD_MODULE));
  const [letterheadPickFor, setLetterheadPickFor] = useState(null);
  // Customer outstanding map, pre-fetched ONLY when the Sale Invoice print has
  // "Previous Balance" switched on. Pre-fetched because printDocument opens a
  // popup — awaiting inside the click would lose the gesture and get blocked.
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);

  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const sort = useTableSort('docDate', 'desc');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: invoices,
    loading,
    refresh: loadInvoices,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingInvoice, setEditingInvoice] = useState(null);
  // Quick-add-item (the "+" beside the line item selector)
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddIndex, setQuickAddIndex] = useState(null);
  const [pendingItemSelect, setPendingItemSelect] = useState(null);
  // Last-4-prices reference shown under each line item (read-only)
  const [priceRefs, setPriceRefs] = useState({});
  const priceReqRef = useRef({});
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fbrSending, setFbrSending] = useState(false);
  const fbrEnabled = isModuleEnabled && isModuleEnabled('fbr_pos');
  // FBR Digital Invoicing — STANDALONE, separate from FBR POS. Off by default.
  const [diSending, setDiSending] = useState(false);
  const diEnabled = isModuleEnabled && isModuleEnabled('fbr_digital_invoicing');
  const [formData, setFormData] = useState({
    docNumber: '',
    docDate: new Date().toISOString().split('T')[0],
    partyId: '',
    partyName: '',
    // Optional per-invoice walk-in details — stored on THIS invoice only, never
    // written to the customer master or the ledger. Shown only when the Walk-in
    // customer is selected.
    walkInName: '',
    walkInPhone: '',
    parentId: '', // DC or SO link
    parentDocNumber: '',
    parentTranType: '', // 'DC' or 'SO'
    parentIds: [], // multi-DC: array of linked DC ids (same customer)
    customerPO: '', // customer's own PO number (optional)
    dueDate: '',
    paymentTerms: '',
    currency: 'PKR',
    discountAmount: 0,
    whtRate: 0,
    whtAmount: 0,
    whtPayableCode: '21203002', // WHT Payable account (add-to-total): fixed to Supplies 21203002
    transportationCharges: 0,
    otherCharges: 0,
    notes: '',
    status: 'Draft',
    // FBR POS: whether this invoice is eligible to be sent to FBR (default ON when
    // the company has the feature). Turn off to skip a non-reportable invoice.
    reportToFbr: true,
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    locationId: '', locationName: '',
    paymentAmount: 0, paymentAccountId: '', paymentAccountName: '',
    existingPaidAmount: 0,
    // Optional GL override — if blank, system defaults to Sales – General (41101001)
    creditAccountId: '', creditAccountCode: '', creditAccountName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);

  const [customers, setCustomers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, errors: 0 });
  const [items, setItems] = useState([]);
  // Customer-wise pricing snapshot. Loaded on customer pick when the
  // sales_customer_pricing module is on; otherwise stays empty (no-op).
  const [customerPricing, setCustomerPricing] = useState(EMPTY_PRICING);
  // Selected customer's default discount% / sales-tax% for line auto-fill.
  // null = not set → the line falls back to the standard rate (item GST / 0%).
  const [customerDefaults, setCustomerDefaults] = useState({ discountPercent: null, taxRate: null, priceType: null });
  const [deliveredDCs, setDeliveredDCs] = useState([]);
  const [approvedSOs, setApprovedSOs] = useState([]);
  // FLW-03: per SO line { ordered, delivered (by other documents), label } for the
  // Sale Order this invoice is made from (null when not linked / not loaded).
  const [soProgress, setSoProgress] = useState(null);
  const soPickRef = useRef('');
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  // All Level-5 revenue-type accounts for the optional credit-account override.
  const [allAccounts, setAllAccounts] = useState([]);
  const [availableBatches, setAvailableBatches] = useState({});  // keyed by itemId
  const [availableSerials, setAvailableSerials] = useState({});  // keyed by itemId
  const [stockBalances, setStockBalances] = useState({});

  // Invoice list now comes from useDocListPagination above (loadInvoices = its
  // refresh). The old full-history getTransactions load lived here.

  // Tier 1 (2026-07-27, Firebase cost): customers/items/accounts come from the
  // device-local master cache (getMaster* → IndexedDB, 0 reads per open with a
  // throttled background change-check) instead of a full getDocs on every open.
  // Each screen keeps its own client-side filter/sort on the returned rows.
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getCustomersCached(activeCompany.id);
      setCustomers([...rows].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '')));
    } catch (error) { console.error('Error loading customers:', error); }
  }, [activeCompany]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  // ── D3 (2026-07-23, Firebase cost): the three loaders below feed ONLY the
  // form's "Link to DC / Link to SO" dropdowns — nothing on the list view uses
  // them. They used to run on every page open AND after every save, each
  // scanning full document history (all DCs + all SOs + every SI *and* NSI
  // ever). They now load ONCE, lazily, the first time a form is actually
  // opened. List browsing and saving cost nothing.
  const formDataLoadedRef = useRef(false);

  const loadDeliveredDCs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Firebase cost: load ONLY still-open (not-yet-invoiced) DCs. Invoicing a DC
      // flips its status to 'Invoiced' (see handleSave), so Delivered/Dispatched
      // ARE exactly the linkable set. This drops the 'Invoiced'-DC read AND removes
      // the need to scan the entire SI/NSI history to detect already-invoiced ones.
      const [delivered, dispatched] = await Promise.all([
        getTransactions(activeCompany.id, { tranTypeCode: 'DC', status: 'Delivered' }),
        getTransactions(activeCompany.id, { tranTypeCode: 'DC', status: 'Dispatched' })
      ]);
      const byId = {};
      [...delivered, ...dispatched].forEach(d => { byId[d.id] = d; });
      setDeliveredDCs(Object.values(byId));
    } catch (error) { console.error('Error loading delivery challans:', error); }
  }, [activeCompany]);

  const loadApprovedSOs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Load approved SOs from unified transactions collection
      const approvedSOsList = await getTransactions(activeCompany.id, {
        tranTypeCode: 'SO', status: 'Approved'
      });
      const partialSOsList = await getTransactions(activeCompany.id, {
        tranTypeCode: 'SO', status: 'Partially Delivered'
      });
      setApprovedSOs([...approvedSOsList, ...partialSOsList]);
    } catch (error) { console.error('Error loading sale orders:', error); }
  }, [activeCompany]);

  // Loads the form-only dropdown data once per page session (see D3 note above).
  const ensureFormData = useCallback(async () => {
    if (formDataLoadedRef.current || !activeCompany?.id) return;
    formDataLoadedRef.current = true;
    try {
      await Promise.all([loadDeliveredDCs(), loadApprovedSOs()]);
    } catch (error) {
      formDataLoadedRef.current = false; // let a later form open retry
      console.error('Error loading form link data:', error);
    }
  }, [activeCompany, loadDeliveredDCs, loadApprovedSOs]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  const loadPaymentAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const allAccts = await getAccountsCached(activeCompany.id);
      const allLvl5 = allAccts.filter(a => a.level === 5 || a.isPostable);
      setPaymentAccounts(allLvl5.filter(a => a.code?.startsWith('11101') || a.code?.startsWith('11102')));
      // Credit-account override picker: revenue (4xxxx) only. Exclude
      // party-linked accounts so users can't accidentally credit a customer
      // master account from here.
      setAllAccounts(allLvl5.filter(a => {
        if (a.linkedEntityType === 'customer' || a.linkedEntityType === 'vendor') return false;
        return a.code?.startsWith('4');
      }).sort((a, b) => (a.code || '').localeCompare(b.code || '')));
    } catch (error) { console.error('Error loading payment accounts:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it (or when the availability check
  // needs an item it doesn't already have).
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  // NOTE: the invoice list itself loads inside useDocListPagination — not here.
  // D3: DC/SO/cross-invoice loaders are NOT here anymore — they load lazily on
  // first form open (ensureFormData), since only the form uses them.
  useEffect(() => {
    loadCustomers(); loadItems();
    loadEmployees(); loadJobs(); loadLocations(); loadPaymentAccounts();
  }, [loadCustomers, loadItems, loadEmployees, loadJobs, loadLocations, loadPaymentAccounts]);

  // Whenever the active inventory location changes (or on edit-load), reload
  // available batches/serials for every tracked line so the popovers show only
  // stock physically present at THIS branch+location — not company-wide.
  useEffect(() => {
    const locId = formData.locationId;
    if (!locId || !activeCompany?.id) return;
    const brId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;
    const batchIds = new Set();
    const serialIds = new Set();
    lineItems.forEach(l => {
      if (!l.itemId) return;
      if (l.trackingType === 'Batch') batchIds.add(l.itemId);
      if (l.trackingType === 'Serial') serialIds.add(l.itemId);
    });
    batchIds.forEach(iid => {
      getAvailableBatches(activeCompany.id, iid, locId, brId).then(b => setAvailableBatches(prev => ({ ...prev, [iid]: b })));
    });
    serialIds.forEach(iid => {
      getAvailableSerials(activeCompany.id, iid, locId, brId).then(s => setAvailableSerials(prev => ({ ...prev, [iid]: s })));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.locationId, activeCompany?.id, activeBranch?.id]);

  // Handle navigation from reports (e.g., Journal Report clicking on document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(transaction => {
      if (transaction) {
        setViewingInvoice(transaction);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  // Filter
  const filteredInvoices = useMemo(() => {
    let filtered = invoices;
    if (statusFilter !== 'All') filtered = filtered.filter(inv => inv.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(inv =>
        inv.docNumber?.toLowerCase().includes(q) ||
        inv.partyName?.toLowerCase().includes(q) ||
        inv.parentDocNumber?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [invoices, statusFilter, searchQuery, sort]);

  // Calculate line. MRP is manually entered per line and contributes to lineTotal.
  // The subTransaction helper credits the MRP component to Sales Revenue (SI)
  // so accounting stays balanced.
  const calculateLine = (line) => {
    const amount = line.quantity * line.unitPrice;
    const discAmt = amount * (line.discountPercent / 100);
    const net = amount - discAmt;
    // GST base:
    //  • 3rd Schedule module ON → per item: 3rd Schedule item = GST on the FULL
    //    price (before discount); Normal item = GST on the price AFTER discount.
    //  • Module OFF → unchanged company behaviour (GST on full price, as live).
    const taxBase = thirdScheduleEnabled
      ? (line.thirdSchedule === true ? amount : net)
      : amount;
    const tax = line.taxable ? taxBase * (line.taxRate / 100) : 0;
    const totalCost = (line.quantity || 0) * (line.unitCost || 0);
    const mrp = Number(line.mrp) || 0;
    return {
      ...line,
      discountAmount: Math.round(discAmt * 100) / 100,
      taxAmount: Math.round(tax * 100) / 100,
      lineTotal: Math.round((net + tax + mrp) * 100) / 100,
      totalCost: Math.round(totalCost * 100) / 100
    };
  };

  // Header totals
  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    const subtotal = lines.reduce((s, l) => s + (l.quantity * l.unitPrice - l.discountAmount), 0);
    const tax = lines.reduce((s, l) => s + l.taxAmount, 0);
    const mrpTotal = lines.reduce((s, l) => s + (Number(l.mrp) || 0), 0);
    const disc = Number(formData.discountAmount) || 0;
    const transportationCharges = Number(formData.transportationCharges) || 0;
    const otherCharges = Number(formData.otherCharges) || 0;
    const beforeWht = subtotal - disc + tax + mrpTotal;
    const whtRate = Number(formData.whtRate) || 0;
    // WHT base: pre-tax (subtotal) by default, or tax-inclusive (subtotal + tax)
    // when the company enables "WHT on tax-inclusive amount" in My Company settings.
    const whtInclusive = typeof window !== 'undefined' && window.__AMAL_WHT_INCLUSIVE__ === true;
    const whtBase = whtInclusive ? (subtotal + tax) : subtotal;
    const wht = Math.round(whtBase * (whtRate / 100) * 100) / 100;
    // Transport + Other are non-taxable add-ons applied AFTER tax & WHT, at the
    // totals level. They raise the customer receivable and post to dedicated
    // income accounts (see subTransactionHelper generateSaleInvoiceEntries).
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmount: Math.round(tax * 100) / 100,
      mrpTotal: Math.round(mrpTotal * 100) / 100,
      headerDiscount: disc,
      whtAmount: wht,
      transportationCharges: Math.round(transportationCharges * 100) / 100,
      otherCharges: Math.round(otherCharges * 100) / 100,
      // WHT direction: subtract (withheld, default) or add (advance tax collected, 236G/H).
      totalAmount: Math.round((beforeWht + (whtAddToTotal ? wht : -wht) + transportationCharges + otherCharges) * 100) / 100
    };
  }, [lineItems, formData.discountAmount, formData.whtRate, formData.transportationCharges, formData.otherCharges, whtAddToTotal]);

  // Form handlers
  const addDays = (isoDate, n) => {
    const days = Number(n);
    if (!isoDate || !days || days <= 0) return '';
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };

  const handleHeaderChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'docDate' && prev.partyId) {
        const party = customers.find(c => c.id === prev.partyId);
        const days = Number(party?.creditDays || party?.paymentTerms) || 0;
        if (days > 0) next.dueDate = addDays(value, days);
      }
      return next;
    });
  };

  const handleCustomerChange = (customerId, party) => {
    // Unified-mode aware: party may be a customer OR a vendor (when unified_party_picker is on).
    // We preserve the original partyType so accounting routes to the correct linked account.
    const resolved = party || customers.find(c => c.id === customerId);
    const isVendor = resolved?._partyType === 'vendor';
    // Customers carry `creditDays`; vendors (unified mode) carry `paymentTerms`.
    const days = Number(resolved?.creditDays || resolved?.paymentTerms) || 0;
    // Auto-fill the salesperson (employee) from the customer's saved default.
    // Only applies when the picked party is a customer with a salesPersonId on
    // record. The dropdown stays editable so the cashier can override per doc.
    const sp = (!isVendor && resolved?.salesPersonId)
      ? { id: resolved.salesPersonId, name: resolved.salesPersonName || '' }
      : null;
    setFormData(prev => ({
      ...prev,
      partyId: customerId,
      partyName: resolved?.companyName || resolved?.vendorName || '',
      partyType: isVendor ? 'vendor' : 'customer',
      // Fresh party → clear any previously-typed walk-in name/phone so they never
      // stick to a different customer.
      walkInName: '', walkInPhone: '',
      paymentTerms: days ? `${days} days` : prev.paymentTerms,
      dueDate: days ? addDays(prev.docDate, days) : prev.dueDate,
      parentId: '', parentDocNumber: '', parentTranType: '',
      ...(sp ? { employeeId: sp.id, employeeName: sp.name } : {})
    }));
    setLineItems([{ ...EMPTY_LINE }]);
    setSoProgress(null);
    soPickRef.current = '';

    // Capture this customer's default discount% / sales-tax% so each added line
    // auto-fills from it. Customers only (not unified-mode vendors); null = not set.
    setCustomerDefaults({
      discountPercent: (!isVendor && resolved?.defaultDiscountPercent != null) ? Number(resolved.defaultDiscountPercent) : null,
      taxRate: (!isVendor && resolved?.defaultTaxRate != null) ? Number(resolved.defaultTaxRate) : null,
      // Customer Class → default price tier for added lines (Retail/Wholesale/Dealer).
      priceType: (!isVendor && resolved?.customerClass) ? resolved.customerClass : null,
    });

    // Load this customer's custom pricing snapshot. No-op when:
    //   - module sales_customer_pricing is OFF
    //   - the picked party is a vendor (unified picker)
    //   - customerId is empty (cleared)
    if (!isVendor && customerId) {
      loadCustomerPricing(activeCompany?.id, customerId, isModuleEnabled)
        .then(setCustomerPricing)
        .catch(err => { console.error('Customer pricing load failed:', err); setCustomerPricing(EMPTY_PRICING); });
    } else {
      setCustomerPricing(EMPTY_PRICING);
    }
  };

  // Customer PO from the linked challan(s) (2026-11-02). A challan saved before
  // it had its own Customer PO takes its Sale Order's — one small read, only
  // when linking, cached per Sale Order. Several challans → each PO once.
  const soPOCacheRef = useRef({});
  const fillCustomerPOFromDCs = async (ids) => {
    const pos = [];
    for (const id of ids) {
      const dc = deliveredDCs.find(d => d.id === id);
      if (!dc) continue;
      let po = customerPOOf(dc);
      if (!po && dc.parentId && activeCompany?.id) {
        if (!(dc.parentId in soPOCacheRef.current)) {
          try {
            const s = await getDoc(doc(db, 'companies', activeCompany.id, 'transactions', dc.parentId));
            soPOCacheRef.current[dc.parentId] = s.exists() ? customerPOOf(s.data()) : '';
          } catch (_) { soPOCacheRef.current[dc.parentId] = ''; }
        }
        po = soPOCacheRef.current[dc.parentId];
      }
      pos.push(po);
    }
    const joined = joinCustomerPOs(pos);
    if (!joined) return;
    setFormData(prev => (JSON.stringify(prev.parentIds || []) === JSON.stringify(ids) ? { ...prev, customerPO: joined } : prev));
  };

  const addDC = (dcId) => {
    if (!dcId) return;
    const dc = deliveredDCs.find(d => d.id === dcId);
    if (!dc) return;
    if (formData.parentIds.includes(dcId)) return;
    if (formData.parentIds.length > 0 && formData.partyId && dc.partyId !== formData.partyId) {
      alert('All linked DCs must belong to the same customer.');
      return;
    }
    const isFirst = formData.parentIds.length === 0;
    const newLines = (dc.lines || []).map((l, idx) => {
      const item = items.find(i => i.id === l.itemId);
      const trackingType = l.trackingType || item?.trackingType || 'None';
      // Scope serial/batch lookup to the DC's source location AND the active
      // branch, so the same physical location used across branches stays isolated.
      const dcLocId = dc.locationId || dc.fromLocationId || formData.locationId || null;
      const brId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;
      if (l.itemId && trackingType === 'Batch') {
        getAvailableBatches(activeCompany.id, l.itemId, dcLocId, brId).then(batches => {
          setAvailableBatches(prev => ({ ...prev, [l.itemId]: batches }));
        });
      } else if (l.itemId && trackingType === 'Serial') {
        getAvailableSerials(activeCompany.id, l.itemId, dcLocId, brId).then(serials => {
          setAvailableSerials(prev => ({ ...prev, [l.itemId]: serials }));
        });
      }
      return {
        ...EMPTY_LINE,
        itemId: l.itemId || '',
        itemCode: l.itemCode || item?.itemCode || '',
        itemName: l.itemName || item?.itemName || '',
        description: l.description || '',
        ...hsCodePatch(l, item),
        quantity: l.quantity || 0,
        unitPrice: l.unitPrice || 0,
        unitCost: Number(l.unitCost) || Number(item?.averageCost) || item?.purchasePrice || 0, // INV-04: the cost the challan took out of stock (its stock row), so profit matches stock
        unitOfMeasure: l.unitOfMeasure || item?.unitOfMeasure || item?.unit || 'Pcs',
        // Carry line discount % + tax forward from the DC (which carried them from the SO)
        // so SO → DC → Invoice keeps the line-level discount and tax. Invoice recomputes
        // the amounts from these drivers.
        discountPercent: l.discountPercent || 0,
        discountAmount: l.discountAmount || 0,
        taxable: l.taxable || false,
        taxRate: l.taxRate ?? 0,
        thirdSchedule: l.thirdSchedule ?? (item?.thirdSchedule === true),
        trackingType,
        batchNumber: l.batchNumber || '',
        serialNumbers: l.serialNumbers || [],
        sourceDocId: dc.id,
        sourceDocNumber: dc.docNumber || '',
        sourceDocDate: dc.docDate || '',
        sourceLineNumber: l.lineNumber || (idx + 1)
      };
    });
    setFormData(prev => ({
      ...prev,
      parentIds: [...prev.parentIds, dcId],
      parentTranType: 'DC',
      parentId: isFirst ? dcId : prev.parentId,
      parentDocNumber: isFirst ? (dc.docNumber || '') : prev.parentDocNumber,
      partyId: isFirst ? dc.partyId : prev.partyId,
      partyName: isFirst ? dc.partyName : prev.partyName,
      locationId: isFirst ? (dc.locationId || dc.fromLocationId || '') : prev.locationId,
      locationName: isFirst ? (dc.locationName || dc.fromLocationName || '') : prev.locationName,
      ...(isFirst ? carryEmployeeJob(dc) : {})
    }));
    setLineItems(prev => {
      const hasOnlyEmpty = prev.length === 1 && !prev[0].itemId && !prev[0].description && !prev[0].sourceDocId;
      return hasOnlyEmpty ? newLines : [...prev, ...newLines];
    });

    fillCustomerPOFromDCs([...formData.parentIds, dcId]);
  };

  const removeDC = (dcId) => {
    const remaining = formData.parentIds.filter(id => id !== dcId);
    const remainingLines = lineItems.filter(l => l.sourceDocId !== dcId);
    if (remaining.length === 0) {
      setFormData(prev => ({
        ...prev,
        parentIds: [],
        parentId: '',
        parentDocNumber: '',
        parentTranType: ''
      }));
      setLineItems([{ ...EMPTY_LINE }]);
      return;
    }
    const firstId = remaining[0];
    const firstDC = deliveredDCs.find(d => d.id === firstId);
    setFormData(prev => ({
      ...prev,
      parentIds: remaining,
      parentId: firstId,
      parentDocNumber: firstDC?.docNumber || ''
    }));
    setLineItems(remainingLines.length ? remainingLines : [{ ...EMPTY_LINE }]);

    fillCustomerPOFromDCs(remaining);
  };

  const handleSOChange = async (parentId) => {
    const so = approvedSOs.find(s => s.id === parentId);
    soPickRef.current = so ? parentId : '';
    if (!so) {
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '', parentTranType: '' }));
      setSoProgress(null);
      return;
    }
    setFormData(prev => ({
      ...prev, parentId, parentDocNumber: so.docNumber || '',
      parentTranType: 'SO',
      partyId: so.partyId, partyName: so.partyName,
      ...(customerPOOf(so) ? { customerPO: customerPOOf(so) } : {}),
      ...carryEmployeeJob(so)
    }));
    // FLW-03: what each SO line already has delivered (by other documents).
    let progress = null;
    try {
      progress = await loadSOProgress(activeCompany.id, so,
        editingInvoice && editingInvoice.parentId === so.id ? editingInvoice : null);
    } catch (e) { console.error('Error loading sale order progress:', e); }
    if (soPickRef.current !== parentId) return; // another order was picked meanwhile
    setSoProgress(progress);
    const remainingOf = (n) => (progress && progress[n]) ? Math.max(0, r6(progress[n].ordered - progress[n].delivered)) : null;
    // Auto-fill lines from SO (with cost lookup). Fully delivered lines are not
    // offered; the rest pre-fill with what is left to deliver.
    const soLines = (so.lines || []).map((l, idx) => ({ ...l, _soLineNo: soLineNo(l, idx) }))
      .filter(l => remainingOf(l._soLineNo) === null || remainingOf(l._soLineNo) > 0);
    if ((so.lines || []).length > 0 && soLines.length === 0) {
      alert(`Every line of Sale Order ${so.docNumber || ''} is already fully delivered.`);
      soPickRef.current = '';
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '', parentTranType: '' }));
      setSoProgress(null);
      setLineItems([{ ...EMPTY_LINE }]);
      return;
    }
    if (soLines.length > 0) {
      setLineItems(soLines.map(l => {
        const item = items.find(i => i.id === l.itemId);
        const trackingType = l.trackingType || item?.trackingType || 'None';
        // Load available batches/serials for tracked items, scoped to the
        // current location AND branch so cross-branch stock isn't mixed in.
        const soLocId = so.locationId || formData.locationId || null;
        const brId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;
        if (l.itemId && trackingType === 'Batch') {
          getAvailableBatches(activeCompany.id, l.itemId, soLocId, brId).then(batches => {
            setAvailableBatches(prev => ({ ...prev, [l.itemId]: batches }));
          });
        } else if (l.itemId && trackingType === 'Serial') {
          getAvailableSerials(activeCompany.id, l.itemId, soLocId, brId).then(serials => {
            setAvailableSerials(prev => ({ ...prev, [l.itemId]: serials }));
          });
        }
        return {
          ...EMPTY_LINE,
          itemId: l.itemId || '',
          itemCode: l.itemCode || item?.itemCode || '',
          itemName: l.itemName || item?.itemName || '',
          description: l.description || '',
          ...hsCodePatch(l, item),
          soLineNumber: l._soLineNo,
          quantity: remainingOf(l._soLineNo) === null ? (l.quantity || 0) : remainingOf(l._soLineNo),
          unitPrice: l.unitPrice || 0,
          unitCost: Number(item?.averageCost) || l.unitCost || item?.purchasePrice || 0, // INV-04: weighted average first
          unitOfMeasure: l.unitOfMeasure || item?.unitOfMeasure || item?.unit || 'Pcs',
          // Carry the SO line's discount (the % is the stored driver; an amount typed
          // on the SO was saved as its %), so SO → Invoice keeps the same amounts.
          discountPercent: Number(l.discountPercent) || 0,
          taxable: l.taxable || false,
          taxRate: l.taxRate ?? 0,
          thirdSchedule: l.thirdSchedule ?? (item?.thirdSchedule === true),
          trackingType,
          batchNumber: l.batchNumber || '',
          serialNumbers: l.serialNumbers || []
        };
      }));
    }
  };

  // When a quick-added item lands in the items list, drop it into the line that
  // triggered the add (runs after setItems, so the price/unit auto-fill works).
  useEffect(() => {
    if (pendingItemSelect && items.some(i => i.id === pendingItemSelect.itemId)) {
      handleLineChange(pendingItemSelect.index, 'itemId', pendingItemSelect.itemId);
      setPendingItemSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pendingItemSelect]);

  const handleQuickItemCreated = (newItem) => {
    setItems(prev => [...prev, newItem].sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    if (quickAddIndex != null) setPendingItemSelect({ index: quickAddIndex, itemId: newItem.id });
    setShowQuickAdd(false);
    setQuickAddIndex(null);
  };

  // Load the last-4 SALE prices for each distinct item on the lines (read-only).
  useEffect(() => {
    if (!activeCompany?.id) return;
    const ids = [...new Set(lineItems.map(l => l.itemId).filter(Boolean))];
    ids.forEach(id => {
      if (priceReqRef.current[id]) return;   // fetch once per item per session
      priceReqRef.current[id] = true;
      getRecentItemPrices(activeCompany.id, id, 'sale').then(hints => {
        setPriceRefs(prev => ({ ...prev, [id]: hints || [] }));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, activeCompany]);

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Line discount can be entered as % OR a rupee amount (they sync). The % is the
      // stored driver; typing an amount back-calculates the % so tax/accounting stay exact.
      if (field === 'discountAmountInput') {
        const gross = (Number(updated[index].quantity) || 0) * (Number(updated[index].unitPrice) || 0);
        const amt = Math.max(0, Number(value) || 0);
        delete updated[index].discountAmountInput; // not a persisted field
        // Shortest % that gives back exactly the typed amount (4 decimals alone
        // could turn 70 into 69.99).
        updated[index].discountPercent = percentForDiscountAmount(gross, amt);
      }

      // Price type change - auto-fill unit price from item
      if (field === 'priceType' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          if (value === 'Retail') updated[index].unitPrice = item.salePrice || 0;
          else if (value === 'Wholesale') updated[index].unitPrice = item.wholesalePrice || 0;
          else if (value === 'Dealer') updated[index].unitPrice = item.dealerPrice || 0;
          // Customer-wise override wins over price tier.
          const ovr = getOverridePrice(customerPricing, updated[index].itemId);
          if (ovr !== null) updated[index].unitPrice = ovr;
        }
      }

      // Unit change — pull unit-specific salePrice/purchasePrice if defined,
      // otherwise scale base salePrice by the conversion factor.
      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item && Array.isArray(item.units)) {
          const unitRow = item.units.find(u => u.unitCode === value);
          if (unitRow) {
            updated[index].unitOfMeasure = unitRow.unitCode;
            const conv = Number(unitRow.conversion) || 1;
            updated[index].unitPrice = Number(unitRow.salePrice) || (Number(item.salePrice) || 0) * conv;
            updated[index].unitCost = Number(item.averageCost) ? Number(item.averageCost) * conv : (Number(unitRow.purchasePrice) || (Number(item.purchasePrice) || 0) * conv); // INV-04
            // Show Two Units: switching "Rate per" between the item's two units keeps the same physical qty
            // (not on lines linked to another document — their qty is tracked against it).
            const twoQ = (formData.parentId || prev[index].sourceDocId || prev[index].poLineId) ? null
              : twoUnitSwitchQty(item, prev[index].unitCode || prev[index].unitOfMeasure, value, prev[index].quantity);
            if (twoQ !== null) updated[index].quantity = twoQ;
            // Customer-wise override wins. NOTE: override is per-item, NOT per-unit
            // — so when the user switches unit, we keep the override price as the
            // item's "headline price" regardless of unit. If a per-unit price
            // is needed in the future, store the override against unitCode too.
            const ovr = getOverridePrice(customerPricing, updated[index].itemId);
            if (ovr !== null) updated[index].unitPrice = ovr;
          }
        }
      }

      // Size-mode line edits: recompute Total SQft (= line.quantity) whenever
      // L / H / Thickness / Pieces / SizeUnit changes. The rest of the line
      // calc (discount / tax / lineTotal) then runs via calculateLine() below.
      if (updated[index].hasSize && ['length', 'height', 'thickness', 'pieces', 'sizeUnit'].includes(field)) {
        updated[index] = recomputeSizeLine(updated[index]);
      }

      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].itemCode = item.itemCode || '';
          updated[index].itemName = item.itemName || '';
          updated[index].description = item.itemName || '';
          updated[index].unitPrice = item.salePrice || 0;
          updated[index].unitCost = Number(item.averageCost) || item.purchasePrice || 0; // Cost for COGS — INV-04: weighted average first

          // Size mode: when the item is flagged hasSize, prime the line with
          // L / H / Thickness defaults from the item master and force the
          // descriptive unit to SQft. unitPrice from the item is treated as
          // the rate per SQft going forward.
          if (item.hasSize) {
            updated[index].hasSize = true;
            updated[index].sizeUnit = item.sizeUnit === 'cm' ? 'cm' : 'inches';
            updated[index].length = Number(item.defaultLength) || 0;
            updated[index].height = Number(item.defaultHeight) || 0;
            updated[index].thickness = Number(item.defaultThickness) || 0;
            updated[index].pieces = updated[index].pieces || 1;
            updated[index].unitOfMeasure = 'SQft';
            updated[index].unitCode = 'SQft';
            updated[index] = recomputeSizeLine(updated[index]);
          } else {
            // Reset size fields when picking a non-size item over a previous
            // size-mode pick on the same line.
            updated[index].hasSize = false;
          }

          // Seed unit fields. For multi-unit items, pick defaultSaleUnit if set,
          // else the base unit. For single-unit, fall back to unitOfMeasure.
          let seededUnit = item.unitOfMeasure || item.unit || 'Pcs';
          if (Array.isArray(item.units) && item.units.length > 0) {
            const baseRow = item.units.find(u => u.isBase) || item.units[0];
            const defaultRow = item.defaultSaleUnit
              ? item.units.find(u => u.unitCode === item.defaultSaleUnit) || baseRow
              : baseRow;
            if (defaultRow) {
              seededUnit = defaultRow.unitCode;
              const conv = Number(defaultRow.conversion) || 1;
              updated[index].unitPrice = Number(defaultRow.salePrice) || (Number(item.salePrice) || 0) * conv;
              updated[index].unitCost = Number(item.averageCost) ? Number(item.averageCost) * conv : (Number(defaultRow.purchasePrice) || (Number(item.purchasePrice) || 0) * conv); // INV-04
            }
          }
          updated[index].unitOfMeasure = seededUnit;
          updated[index].unitCode = seededUnit;
          // Customer Class Pricing: default the price tier from the customer's class
          // (module on); fall back to the seeded Retail price if that tier is unset.
          const cls = priceClassEnabled ? customerDefaults.priceType : null;
          updated[index].priceType = cls || 'Retail';
          if (cls === 'Wholesale') updated[index].unitPrice = item.wholesalePrice || updated[index].unitPrice;
          else if (cls === 'Dealer') updated[index].unitPrice = item.dealerPrice || updated[index].unitPrice;
          // Sales tax: a customer-level rate (when set) OVERRIDES the item's GST;
          // customer 0 = tax-exempt. Blank on the customer → item's own GST.
          if (customerDefaults.taxRate != null) {
            updated[index].taxRate = customerDefaults.taxRate;
            updated[index].taxable = customerDefaults.taxRate > 0;
          } else {
            updated[index].taxRate = item.gstRate || 0;
            updated[index].taxable = (item.gstRate || 0) > 0;
          }
          // Discount: a customer-level default seeds the line (blank → stays 0).
          if (customerDefaults.discountPercent != null) {
            updated[index].discountPercent = customerDefaults.discountPercent;
          }
          // Customer Discount by Sub-Category (module): the item's sub-category %
          // for this customer wins; else the default above; else 0.
          const scd = subCategoryLineDiscount(customers.find(c => c.id === formData.partyId), item, isModuleEnabled, customerDefaults.discountPercent);
          if (scd !== undefined) updated[index].discountPercent = scd;
          updated[index].hsCode = item.hsCode || '';
          updated[index].thirdSchedule = item.thirdSchedule === true;
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].batchNumber = '';
          updated[index].serialNumbers = [];
          // Customer-wise pricing override — wins over salePrice / per-unit price.
          const ovr = getOverridePrice(customerPricing, value);
          if (ovr !== null) updated[index].unitPrice = ovr;
          // Load available batches/serials for this item, scoped to the
          // current location AND branch so cross-branch stock isn't mixed in.
          const curLocId = formData.locationId || null;
          const brId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;
          if (item.trackingType === 'Batch') {
            getAvailableBatches(activeCompany.id, value, curLocId, brId).then(batches => {
              setAvailableBatches(prev => ({ ...prev, [value]: batches }));
              // FIFO auto-fill: select oldest batch
              if (batches.length > 0) {
                setLineItems(prev => {
                  const u = [...prev];
                  if (u[index] && u[index].itemId === value) {
                    u[index] = { ...u[index], batchNumber: batches[0].batchNumber };
                  }
                  return u;
                });
              }
            });
          } else if (item.trackingType === 'Serial') {
            getAvailableSerials(activeCompany.id, value, curLocId, brId).then(serials => {
              setAvailableSerials(prev => ({ ...prev, [value]: serials }));
            });
          }
        }
      }
      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const handlePriceBlur = (index) => {
    const line = lineItems[index];
    if (line.itemId && line.unitPrice > 0) {
      const item = items.find(i => i.id === line.itemId);
      if (item && item.minimumPrice > 0 && line.unitPrice < item.minimumPrice) {
        if (!window.confirm(`Price ${line.unitPrice} is below minimum price ${item.minimumPrice} for ${item.itemName}. Continue?`)) {
          setLineItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], unitPrice: item.minimumPrice };
            updated[index] = calculateLine(updated[index]);
            return updated;
          });
        }
      }
    }
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  // Refs to first Item cell of each line row, so we can auto-focus after adding a new row
  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);

  // After adding a new row, focus its Item cell
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  // Triggered from Enter on the last field of a line item
  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (formData.parentId) return;
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0 && Number(line.unitPrice) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      const addNew = window.confirm('Add a new line item?');
      if (addNew) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      // Not last row — just move focus to the next row's Item cell
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };

  const resetForm = () => {
    setFormData({
      docNumber: '', docDate: new Date().toISOString().split('T')[0],
      partyId: '', partyName: '',
      walkInName: '', walkInPhone: '',
      parentId: '', parentDocNumber: '', parentTranType: '', parentIds: [],
      customerPO: '',
      dueDate: '', paymentTerms: '', currency: activeCompany?.currencyCode || 'PKR',
      discountAmount: 0, whtRate: 0, whtAmount: 0, whtPayableCode: '21203002', transportationCharges: 0, otherCharges: 0, notes: '', status: 'Draft',
      employeeId: '', employeeName: '', jobId: '', jobName: '',
      locationId: '', locationName: '',
      paymentAmount: 0, paymentAccountId: '', paymentAccountName: '',
      existingPaidAmount: 0,
      creditAccountId: '', creditAccountCode: '', creditAccountName: ''
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingInvoice(null);
    setSoProgress(null);
    soPickRef.current = '';
  };

  const handleCreate = () => { resetForm(); ensureFormData(); setViewMode('form'); };

  const handleEdit = (inv) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    // FBR-posted invoices are already filed with FBR — warn that edits here won't
    // change the FBR record (the fiscal number stays fixed to the original data).
    // ACC-16: an invoice registered with FBR is locked — correct it with a Sale Return.
    if (inv.fbrInvoiceNumber || inv.diIrn) {
      alert(`This invoice is registered with FBR (${inv.fbrInvoiceNumber ? 'FBR #' + inv.fbrInvoiceNumber : 'IRN ' + inv.diIrn}) and cannot be edited. Issue a Sale Return instead.`);
      return;
    }
    ensureFormData(); // D3: load DC/SO link data on demand
    // Firebase cost: this invoice's linked DC(s) are now status 'Invoiced', so
    // they are NOT in the open-DC list. Fetch just those by id (a few reads, only
    // when opening a DC-linked invoice) so their chips render on the form.
    const linkedIds = Array.isArray(inv.parentIds) && inv.parentIds.length > 0
      ? inv.parentIds
      : (inv.parentTranType === 'DC' && inv.parentId ? [inv.parentId] : []);
    if (linkedIds.length > 0 && activeCompany?.id) {
      Promise.all(linkedIds.map(id => getTransaction(activeCompany.id, id).catch(() => null)))
        .then(docs => {
          const found = docs.filter(Boolean);
          if (found.length) setDeliveredDCs(prev => {
            const byId = {}; [...prev, ...found].forEach(d => { byId[d.id] = d; });
            return Object.values(byId);
          });
        });
    }
    setEditingInvoice(inv);
    setFormData({
      docNumber: inv.docNumber || '',
      docDate: inv.docDate || new Date().toISOString().split('T')[0],
      partyId: inv.partyId || '', partyName: inv.partyName || '',
      walkInName: inv.walkInName || '', walkInPhone: inv.walkInPhone || '',
      partyType: inv.partyType || 'customer',
      parentId: inv.parentId || '', parentDocNumber: inv.parentDocNumber || '',
      parentTranType: inv.parentTranType || '',
      parentIds: Array.isArray(inv.parentIds) && inv.parentIds.length > 0
        ? inv.parentIds
        : (inv.parentTranType === 'DC' && inv.parentId ? [inv.parentId] : []),
      customerPO: inv.customerPO || '',
      dueDate: inv.dueDate || '', paymentTerms: inv.paymentTerms || '',
      currency: inv.currency || 'PKR', discountAmount: inv.discountAmount || 0,
      whtRate: inv.whtRate || 0, whtAmount: inv.whtAmount || 0,
      whtPayableCode: '21203002',
      transportationCharges: inv.transportationCharges || 0, otherCharges: inv.otherCharges || 0,
      notes: inv.notes || '', status: inv.status || 'Draft',
      reportToFbr: inv.reportToFbr !== false,
      employeeId: inv.employeeId || '', employeeName: inv.employeeName || '',
      jobId: inv.jobId || '', jobName: inv.jobName || '',
      locationId: inv.locationId || '', locationName: inv.locationName || '',
      // One-time inline receipt: load the existing inline amount so it stays
      // editable. existingPaidAmount holds ONLY the voucher-allocated portion
      // (paidAmount − inline), which is preserved and not editable here.
      paymentAmount: inv.paymentAmount || 0, paymentAccountId: inv.paymentAccountId || '', paymentAccountName: inv.paymentAccountName || '',
      existingReturnedAmount: Number(inv.returnedAmount) || 0, // ACC-11
      existingPaidAmount: Math.max(0, (inv.paidAmount || 0) - (inv.paymentAmount || 0)),
      creditAccountId: inv.creditAccountId || '',
      creditAccountCode: inv.creditAccountCode || '',
      creditAccountName: inv.creditAccountName || ''
    });
    setLineItems(inv.lines?.length > 0 ? inv.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    // FLW-03: show / check what is left on the linked Sale Order (1 read).
    setSoProgress(null);
    soPickRef.current = (inv.parentTranType === 'SO' && inv.parentId) ? inv.parentId : '';
    if (inv.parentTranType === 'SO' && inv.parentId && activeCompany?.id) {
      (async () => {
        try {
          const snap = await getDoc(doc(db, 'companies', activeCompany.id, 'transactions', inv.parentId));
          if (!snap.exists() || snap.data().tranTypeCode !== 'SO') return;
          const so = { id: snap.id, ...snap.data() };
          const progress = await loadSOProgress(activeCompany.id, so, inv);
          if (soPickRef.current !== inv.parentId) return;
          setSoProgress(progress);
          // Older invoices: tag each line with the SO line it belongs to.
          const tags = tagSOLineNumbers(so.lines || [], inv.lines || [], progress);
          setLineItems(prev => prev.map((l, i) => (Number(l.soLineNumber) > 0 || !tags[i]) ? l : { ...l, soLineNumber: tags[i] }));
        } catch (e) { console.error('Error loading sale order progress:', e); }
      })();
    }
    // Re-hydrate the customer pricing snapshot so the picker filters and any
    // line-level edits behave the same as a fresh customer pick.
    if (inv.partyId && inv.partyType !== 'vendor') {
      loadCustomerPricing(activeCompany?.id, inv.partyId, isModuleEnabled)
        .then(setCustomerPricing)
        .catch(() => setCustomerPricing(EMPTY_PRICING));
    } else {
      setCustomerPricing(EMPTY_PRICING);
    }
    setViewMode('form');
  };

  const handleView = (inv) => { setViewingInvoice(inv); setViewMode('view'); };

  // FBR POS: send a posted Sale Invoice to FBR (separate, deliberate action — not
  // part of Post). The Cloud Function holds the secret token, posts to FBR, and
  // stamps the fiscal number back on the transaction. Allow + retry on failure.
  const handleSendToFbr = async (inv) => {
    if (!inv?.id || !activeCompany?.id || fbrSending) return;
    if (inv.fbrInvoiceNumber) { alert('This invoice is already on FBR.\nFBR Invoice #: ' + inv.fbrInvoiceNumber); return; }
    if (!window.confirm('Send this invoice to FBR now?')) return;
    setFbrSending(true);
    try {
      const fn = httpsCallable(functions, 'postFbrPosInvoice');
      const res = await fn({ companyId: activeCompany.id, transactionId: inv.id });
      const num = res?.data?.fbrInvoiceNumber;
      const patch = { fbrInvoiceNumber: num, fbrStatus: 'posted', fbrPostedAt: new Date().toISOString() };
      setViewingInvoice(prev => (prev && prev.id === inv.id) ? { ...prev, ...patch } : prev);
      if (typeof applyLocal === 'function') { try { applyLocal(inv.id, patch); } catch (_) { /* non-fatal */ } }
      alert('Sent to FBR successfully.\nFBR Invoice #: ' + num);
    } catch (e) {
      const raw = e && e.details && e.details.fbrRaw;
      const msg = (e && e.message) ? e.message : 'Please try again.';
      alert('Could not send to FBR:\n' + msg + (raw ? '\n\n── FBR response ──\n' + raw : ''));
    } finally {
      setFbrSending(false);
    }
  };

  // FBR Digital Invoicing — send this invoice to FBR PRAL (production), store the IRN.
  const handleSendToDi = async (inv) => {
    if (!inv?.id || !activeCompany?.id || diSending) return;
    if (inv.diIrn) { alert('This invoice is already registered with FBR.\nFBR IRN: ' + inv.diIrn); return; }
    if (!window.confirm('Send this invoice to FBR Digital Invoicing now?')) return;
    setDiSending(true);
    try {
      const fn = httpsCallable(functions, 'postDiInvoice');
      const res = await fn({ companyId: activeCompany.id, transactionId: inv.id });
      const irn = res?.data?.diIrn;
      const patch = { diIrn: irn, diStatus: 'posted', diPostedAt: new Date().toISOString() };
      setViewingInvoice(prev => (prev && prev.id === inv.id) ? { ...prev, ...patch } : prev);
      if (typeof applyLocal === 'function') { try { applyLocal(inv.id, patch); } catch (_) { /* non-fatal */ } }
      alert('Sent to FBR Digital Invoicing successfully.\nFBR IRN: ' + irn);
    } catch (e) {
      const raw = e && e.details && e.details.fbrRaw;
      const msg = (e && e.message) ? e.message : 'Please try again.';
      alert('Could not send to FBR Digital Invoicing:\n' + msg + (raw ? '\n\n── FBR response ──\n' + raw : ''));
    } finally {
      setDiSending(false);
    }
  };

  // Save invoice
  const handleSave = async (saveStatus) => {
    const _screenTimer = startSaveTimer('Whole click: Sale Invoice'); // save timer — see console
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.partyId) { alert('Please select a customer'); return; }
    if (Number(formData.paymentAmount) > 0 && !formData.paymentAccountId) {
      alert('Please select a payment account'); return;
    }
    // Zero price allowed (free items) — negative prices still left out. See utils/zeroPriceLines.js.
    const validLines = lineItems.filter(l => l.description && l.quantity > 0 && Number(l.unitPrice) >= 0);
    if (validLines.length === 0) {
      alert('Please add at least one line item with description, quantity, and price');
      return;
    }
    if (!confirmZeroPriceLines(validLines, { fbr: !!(fbrEnabled || diEnabled) })) return;

    // Warn about zero cost items (profit will show 100%)
    const zeroCostItems = validLines.filter(l => !l.unitCost || l.unitCost === 0);
    if (zeroCostItems.length > 0 && saveStatus === 'Posted') {
      const itemNames = zeroCostItems.map(l => l.itemName || l.description).join(', ');
      const proceed = window.confirm(
        `Warning: The following items have ZERO cost price:\n\n${itemNames}\n\nProfit reports will show 100% margin for these items. Consider adding a purchase invoice first.\n\nDo you want to continue?`
      );
      if (!proceed) return;
    }

    // Validate financial year
    if (!activeFinancialYear?.id) {
      alert('No active financial year found. Please set an active financial year in Settings > Financial Year.');
      return;
    }

    // Validate batch/serial tracking
    for (const line of validLines) {
      if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) {
        alert(`Batch selection is required for "${line.itemName || line.description}"`); return;
      }
      if (line.trackingType === 'Serial') {
        const serials = line.serialNumbers || [];
        const qty = line.quantity || 0;
        if (serials.length !== qty) {
          alert(`"${line.itemName || line.description}" requires exactly ${qty} serial number(s). You selected ${serials.length}.`); return;
        }
      }
    }

    // Discount cap validation (max 50%)
    for (const line of validLines) {
      if (Number(line.discountPercent) > 50) {
        alert(`Discount on "${line.itemName || line.description}" exceeds 50%. Please review.`); return;
      }
    }

    // FLW-03: never invoice more than what is left on the Sale Order.
    if (formData.parentTranType === 'SO' && formData.parentId && soProgress) {
      const overMsg = soOverDeliveryMessage(validLines, soProgress, formData.parentDocNumber);
      if (overMsg) { alert(overMsg); return; }
    }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    // Zero-amount check when posting
    const preTotal = validLines.reduce((s, l) => s + (l.quantity * l.unitPrice), 0);
    if (saveStatus === 'Posted' && preTotal <= 0) {
      alert('Cannot post a zero-amount invoice'); return;
    }

    // Stock availability + Credit limit checks in PARALLEL (only when posting)
    if (saveStatus === 'Posted') {
      const stockItems = (!formData.parentId)
        ? validLines.filter(l => l.itemId).map(l => ({ itemId: l.itemId, itemName: l.itemName || l.description, quantity: l.quantity, unitCode: l.unitCode || l.unitOfMeasure }))
        : [];

      // Firebase cost: reuse the on-screen stock badges (+ cached items for unit
      // conversion) instead of re-reading every item's full stock history at Post.
      const itemsById = {};
      items.forEach(i => { itemsById[i.id] = i; });

      const [insufficient, creditResult] = await Promise.all([
        stockItems.length > 0
          ? checkStockAvailability(activeCompany.id, stockItems, { stockMap: stockBalances, itemsMap: itemsById, branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null })
          : Promise.resolve([]),
        formData.partyId
          ? checkCustomerCreditLimit(activeCompany.id, formData.partyId, preTotal, editingInvoice?.id)
          : Promise.resolve({ exceeded: false })
      ]);

      if (insufficient.length > 0) {
        const msg = insufficient.map(i => `${i.itemName}: available ${i.available} ${i.unit || ''}, requested ${i.requested} ${i.unit || ''}`).join('\n');
        if (isModuleEnabled('inventory_block_negative')) {
          alert(`Cannot post — would drive stock below zero:\n\n${msg}`);
          return;
        }
        if (!window.confirm(`Insufficient stock:\n${msg}\n\nContinue anyway?`)) return;
      }

      if (creditResult.exceeded) {
        if (!window.confirm(`Customer credit limit exceeded!\nLimit: ${creditResult.creditLimit.toLocaleString()}\nOutstanding: ${creditResult.outstanding.toLocaleString()}\nNew total: ${creditResult.newTotal.toLocaleString()}\n\nContinue anyway?`)) return;
      }
    }

    // Large transaction warning
    if (isLargeTransaction(preTotal)) {
      if (!window.confirm(`This is a large transaction (${activeCompany?.currencySymbol || 'Rs.'} ${preTotal.toLocaleString()}). Please verify the amount. Continue?`)) return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => ({ ...calculateLine(l), lineNumber: i + 1 }));
      const status = saveStatus || formData.status || 'Draft';

      const invData = {
        tranTypeCode: TRAN_TYPE_CODE,
        docDate: formData.docDate,
        // Unified party: honor the picked party's real type (a vendor selected as
        // "customer" must post to the vendor's own account for one shared ledger),
        // not a hardcoded 'customer'.
        partyType: formData.partyType || 'customer',
        partyId: formData.partyId,
        partyName: formData.partyName,
        // Per-invoice walk-in details — saved ONLY on this invoice (only kept for
        // the Walk-in customer; blank for anyone else). Never touches the customer
        // master or the ledger.
        walkInName: (customers.find(c => c.id === formData.partyId)?.isWalkIn) ? (formData.walkInName || '') : '',
        walkInPhone: (customers.find(c => c.id === formData.partyId)?.isWalkIn) ? (formData.walkInPhone || '') : '',
        parentId: formData.parentId || null,
        parentDocNumber: formData.parentDocNumber || '',
        parentTranType: formData.parentTranType || '',
        parentIds: formData.parentIds || [],
        customerPO: formData.customerPO || '',
        dueDate: formData.dueDate,
        paymentTerms: formData.paymentTerms,
        currency: formData.currency,
        subtotal: totals.subtotal,
        discountAmount: totals.headerDiscount,
        taxAmount: totals.taxAmount,
        whtRate: Number(formData.whtRate) || 0,
        whtAmount: totals.whtAmount,
        whtAddToTotal: whtAddToTotal, // direction at save time → drives accounting + print sign
        // Chosen WHT Payable account (only meaningful in add-to-total direction);
        // Fixed to WHT Payable - Supplies (21203002).
        whtPayableCode: whtAddToTotal ? '21203002' : null,
        whtLabel: whtLabel, // snapshot the label shown on this invoice
        transportationCharges: totals.transportationCharges,
        otherCharges: totals.otherCharges,
        totalAmount: totals.totalAmount,
        paidAmount: (Number(formData.existingPaidAmount) || 0) + (Number(formData.paymentAmount) || 0),
        // ACC-11: returns reduce the balance but are not 'paid'; never below zero.
        balanceAmount: Math.max(0, totals.totalAmount - (Number(formData.existingPaidAmount) || 0) - (Number(formData.paymentAmount) || 0) - (Number(formData.existingReturnedAmount) || 0)),
        paymentAmount: Number(formData.paymentAmount) || 0,
        paymentAccountId: formData.paymentAccountId || null,
        paymentAccountName: formData.paymentAccountName || null,
        creditAccountId: formData.creditAccountId || null,
        creditAccountCode: formData.creditAccountCode || null,
        creditAccountName: formData.creditAccountName || null,
        employeeId: formData.employeeId,
        employeeName: formData.employeeName,
        jobId: formData.jobId,
        jobName: formData.jobName,
        locationId: formData.locationId,
        locationName: formData.locationName,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null,
        notes: formData.notes,
        status,
        reportToFbr: formData.reportToFbr !== false,
        lines: calculatedLines,
        financialYearId: activeFinancialYear.id
      };

      if (status === 'Posted' && formData.status !== 'Posted') {
        invData.postedBy = currentUser.uid;
        invData.postedAt = new Date().toISOString();
      }

      let savedDocId = editingInvoice?.id;
      let savedDocNumber = editingInvoice?.docNumber;
      let newTranData = null;

      if (editingInvoice) {
        await updateTransaction(activeCompany.id, editingInvoice.id, invData, currentUser.uid);
      } else {
        invData.paidAmount = Number(formData.paymentAmount) || 0;
        invData.balanceAmount = totals.totalAmount - (Number(formData.paymentAmount) || 0);
        newTranData = await createTransaction(activeCompany.id, invData, currentUser.uid);
        savedDocId = newTranData.id;
        savedDocNumber = newTranData.docNumber;
      }

      // Firebase cost: keep the in-memory stock badges correct after a sale that
      // NEWLY reduced stock, so the next invoice this session checks fresh numbers.
      // Direct invoices only — a DC-linked invoice already moved stock via the DC.
      if (status === 'Posted' && formData.status !== 'Posted' && !formData.parentId) {
        setStockBalances(prev => {
          const next = { ...prev };
          calculatedLines.forEach(l => {
            if (!l.itemId) return;
            const it = items.find(i => i.id === l.itemId);
            if (it?.itemType === 'service') return;
            // On-demand stock: only adjust items whose balance we actually know.
            // Decrementing an unknown item would invent a bogus (negative) figure
            // that the next availability check would trust — leave it unknown and
            // let it be fetched fresh when needed.
            if (next[l.itemId] === undefined) return;
            const { requestedBase } = toBaseQty(it || {}, l.quantity, l.unitCode || l.unitOfMeasure);
            next[l.itemId] = Number(next[l.itemId]) - requestedBase;
          });
          return next;
        });
      }

      // Update each linked DC's status to 'Invoiced' when posting.
      // For edits, revert DCs that were dropped from parentIds (back to 'Delivered').
      if (formData.parentTranType === 'DC' && status === 'Posted') {
        try {
          const newIds = formData.parentIds || [];
          const oldIds = (editingInvoice?.parentIds && editingInvoice.parentIds.length > 0)
            ? editingInvoice.parentIds
            : (editingInvoice?.parentTranType === 'DC' && editingInvoice?.parentId ? [editingInvoice.parentId] : []);
          const removedIds = oldIds.filter(id => !newIds.includes(id));
          // Speed: all linked challans are updated at the same time (was one by one).
          await Promise.all([
            ...removedIds.map(dcId => updateTransactionStatus(activeCompany.id, dcId, 'Delivered', currentUser.uid)),
            ...newIds.map(dcId => updateTransactionStatus(activeCompany.id, dcId, 'Invoiced', currentUser.uid)),
          ]);
        } catch (err) { console.error('Error updating DC status:', err); }
      }

      // Update parent SO line quantities and status if linked directly (without
      // DC) — matched by the SO line each invoice line came from (FLW-03); an edit
      // takes the invoice's previous quantities off first. Only a posted invoice counts.
      if ((formData.parentId && formData.parentTranType === 'SO') || (editingInvoice && editingInvoice.parentTranType === 'SO')) {
        try {
          await syncInvoiceSOProgress(activeCompany.id, editingInvoice,
            { parentId: formData.parentId, parentTranType: formData.parentTranType, status, lines: calculatedLines },
            currentUser.uid);
        } catch (err) { console.error('Error updating Sale Order status:', err); }
      }

      // SubTransactions + StockFlow already generated by createTransaction/updateTransaction above.
      // No duplicate calls needed here.

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingInvoice
        ? { ...editingInvoice, ...invData, id: savedDocId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingInvoice
        ? prev.map(r => r.id === savedDocId ? savedRecord : r)
        : [savedRecord, ...prev]);

      // Stock moved — drop the per-item cached quantities so the next "stock?"
      // lookup (here or on any other form this session) reads fresh numbers.
      invalidateItemStock(activeCompany.id);

      setViewMode('list');
      resetForm();

      // The DC(s) this invoice just consumed are now 'Invoiced' — drop them from
      // the open-DC picker so they can't be linked again this session (no re-scan
      // of the invoice history needed).
      const usedDCIds = (savedRecord?.parentIds && savedRecord.parentIds.length > 0)
        ? savedRecord.parentIds
        : (savedRecord?.parentTranType === 'DC' && savedRecord?.parentId ? [savedRecord.parentId] : []);
      if (savedRecord?.status === 'Posted' && usedDCIds.length > 0) {
        const used = new Set(usedDCIds);
        setDeliveredDCs(prev => prev.filter(d => !used.has(d.id)));
      }

      // Background refresh — reconciles the list with server data (not blocking).
      loadInvoices().catch(err => console.error('Background refresh failed:', err));
    } catch (error) {
      console.error('Error saving sale invoice:', error);
      alert('Error saving sale invoice: ' + error.message);
    } finally {
      _screenTimer.end();
      setSaving(false);
    }
  };

  const toggleSelectAll = () => {
    const eligible = filteredInvoices.filter(i => i.status !== 'Draft' && i.status !== 'Cancelled');
    if (selectedIds.size === eligible.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligible.map(i => i.id)));
    }
  };
  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const handleBulkWhatsApp = async () => {
    if (!isModuleEnabled('whatsapp_connect')) {
      alert('WhatsApp module is not enabled.'); return;
    }
    const items = filteredInvoices.filter(i => selectedIds.has(i.id));
    const withPhone = items.filter(i => {
      const c = customers.find(x => x.id === i.partyId);
      return c && (c.phone || c.mobile);
    });
    if (withPhone.length === 0) { alert('None of the selected invoices have a customer with a phone number.'); return; }
    if (!window.confirm(`Send ${withPhone.length} invoice(s) on WhatsApp? Will skip ${items.length - withPhone.length} without phone.`)) return;
    setBulkSending(true);
    setBulkProgress({ done: 0, total: withPhone.length, errors: 0 });
    let errors = 0;
    for (let i = 0; i < withPhone.length; i++) {
      const inv = withPhone[i];
      const customer = customers.find(c => c.id === inv.partyId);
      try {
        await sendDocumentViaWhatsApp({ templateKey: 'saleInvoice', doc: inv, party: customer, activeCompany });
      } catch (e) {
        console.error('Bulk send error', inv.docNumber, e);
        errors++;
      }
      setBulkProgress({ done: i + 1, total: withPhone.length, errors });
    }
    setBulkSending(false);
    setSelectedIds(new Set());
    alert(`Bulk send complete. Sent: ${withPhone.length - errors}, Failed: ${errors}.`);
  };

  const handleDelete = async (inv) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    // FBR fiscal invoices are permanent — never allow deletion of an FBR-posted doc.
    if (inv.fbrInvoiceNumber) { alert(`This invoice is registered with FBR (FBR #${inv.fbrInvoiceNumber}) and cannot be deleted. Issue a Sale Return instead.`); return; }
    if (inv.diIrn) { alert(`This invoice is registered with FBR Digital Invoicing (IRN ${inv.diIrn}) and cannot be deleted. Issue a Sale Return instead.`); return; }
    if (!window.confirm(`Delete Invoice ${inv.docNumber}?`)) return;
    try {
      // ACC-04 fix (2026-09-30): deleteTransaction() runs the safety checks FIRST
      // (linked documents, receipts, FBR) and only then removes ledger + stock rows.
      // Wiping them here beforehand left a blocked document with no accounting.
      await deleteTransaction(activeCompany.id, inv.id);
      await reverseSODelivery(inv);
      await reverseDCInvoicing(inv);
      // D3: DC statuses changed — mark the form link-data stale so the NEXT
      // form open refetches it, instead of paying for it here on the list view.
      formDataLoadedRef.current = false;
      await loadInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert(error.message || 'Failed to delete invoice.');
    }
  };

  const handleCancel = async (inv) => {
    if (!canCancel) { alert('You do not have permission to cancel.'); return; }
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    // FBR fiscal invoices are permanent — cancel via a Sale Return, not by voiding.
    if (inv.fbrInvoiceNumber) { alert(`This invoice is registered with FBR (FBR #${inv.fbrInvoiceNumber}) and cannot be cancelled. Issue a Sale Return instead.`); return; }
    if (inv.diIrn) { alert(`This invoice is registered with FBR Digital Invoicing (IRN ${inv.diIrn}) and cannot be cancelled. Issue a Sale Return instead.`); return; }
    if (inv.status === 'Paid' || inv.status === 'Cancelled') {
      alert('This invoice cannot be cancelled');
      return;
    }
    if ((inv.paidAmount || 0) > 0) {
      alert('Cannot cancel an invoice with payments. Reverse payments first.');
      return;
    }
    if (!window.confirm(`Cancel Invoice ${inv.docNumber}?`)) return;
    try {
      // Delete sub-transactions and stock flow if posted
      if (inv.status === 'Posted' || inv.status === 'Partial') {
        await deleteSubTransactions(activeCompany.id, inv.id);
        await deleteStockFlow(activeCompany.id, inv.id);
      }
      await updateTransactionStatus(activeCompany.id, inv.id, 'Cancelled', currentUser.uid);
      await reverseSODelivery(inv);
      await reverseDCInvoicing(inv);
      formDataLoadedRef.current = false; // D3: refresh link data on next form open
      await loadInvoices();
    } catch (error) { console.error('Error cancelling invoice:', error); }
  };

  // Revert linked DC(s) back to 'Delivered' when SI is deleted or cancelled.
  const reverseDCInvoicing = async (inv) => {
    if (inv.parentTranType !== 'DC') return;
    const ids = (inv.parentIds && inv.parentIds.length > 0)
      ? inv.parentIds
      : (inv.parentId ? [inv.parentId] : []);
    if (ids.length === 0) return;
    try {
      for (const dcId of ids) {
        await updateTransactionStatus(activeCompany.id, dcId, 'Delivered', currentUser.uid);
      }
    } catch (err) { console.error('Error reverting DC status:', err); }
  };

  // Reverse SO deliveredQty when SI is deleted or cancelled (only a posted
  // invoice had added to the SO; matched by the SO line each line came from).
  const reverseSODelivery = async (inv) => {
    if (!inv.parentId || inv.parentTranType !== 'SO') return;
    try {
      await syncInvoiceSOProgress(activeCompany.id, inv, null, currentUser.uid);
    } catch (err) { console.error('Error reversing SO delivery:', err); }
  };

  // Print
  // Invoice Letterhead Choice (advance module): Print first asks whose details
  // go at the top — the company's, another saved name, or Blank.
  //   letterhead undefined → ask;  null → company (as before);  object → that one.
  const handlePrint = async (inv, letterhead) => {
    if (letterheadChoiceOn && letterhead === undefined) { setLetterheadPickFor({ inv: inv || viewingInvoice }); return; }
    const target = inv || viewingInvoice;
    const cust = customers.find(c => c.id === target?.partyId);
    const enrichedLines = (target?.lines || []).map(l => {
      const it = items.find(i => i.id === l.itemId);
      // Barcode from the item master (print-only, 0 reads): the sold unit's own
      // barcode for multi-unit items, else the item's main barcode. Printed only
      // when Print Settings → Sale Invoice → Barcode is switched on.
      const unitBarcode = (it?.units || []).find(u => u.unitCode === (l.unitCode || l.unitOfMeasure))?.barcode;
      return { ...l, imageUrl: it?.imageUrl || l.imageUrl || '', barcode: unitBarcode || it?.barcode || l.barcode || '' };
    });

    // Previous balance = what the customer owed BEFORE this bill. Fetched
    // on demand for THIS customer only (~10 rows) at print time — the old
    // page-mount bulk load scanned the whole year's ledger on every SI open,
    // and its preloaded figure went stale if you posted then printed.
    // The live balance already includes this invoice once it's in the ledger,
    // so strip this invoice's own outstanding back out; a Draft isn't in the
    // ledger yet.
    let previousBalance = null;
    if (printConfig?.saleInvoice?.showPreviousBalance && target?.partyId) {
      let cur = NaN;
      try {
        cur = Number(await getCustomerBalance(
          activeCompany.id,
          target.partyId,
          activeBranch && activeBranch.id !== 'all' ? activeBranch.id : null,
          activeFinancialYear?.id || null
        ));
      } catch { /* balance unavailable — print without it */ }
      if (!Number.isNaN(cur)) {
        const total = Number(target.totalAmount) || 0;
        const paid = Number(target.paidAmount) || 0;
        const inLedger = ['Posted', 'Partial', 'Paid'].includes(target.status);
        previousBalance = inLedger ? cur - (total - paid) : cur;
      }
    }

    // Resolve the customer's area/region names (IDs on the customer) for the printout.
    const { areas, regions } = await getAreasRegionsCached(activeCompany.id);
    const partyArea = areas.find(a => a.id === cust?.areaId)?.areaName || '';
    const partyRegion = regions.find(r => r.id === cust?.regionId)?.regionName || '';
    const enriched = {
      ...target,
      lines: enrichedLines,
      partyNTN: cust?.ntn || target?.partyNTN || '',
      partySTRN: cust?.strn || target?.partySTRN || '',
      partyAddress: cust?.address || target?.partyAddress || '',
      partyPhone: cust?.phone || cust?.mobile || target?.partyPhone || '',
      partyArea,
      partyRegion,
      ...(previousBalance != null ? { previousBalance } : {}),
    };
    // FBR POS: fbrInvoiceNumber / fbrPosId ride along via ...target; generate the
    // QR of the fiscal number here (async) so the print block can show it.
    if (target?.fbrInvoiceNumber) {
      try { enriched.fbrQr = await QRCode.toDataURL(String(target.fbrInvoiceNumber), { margin: 1, width: 160 }); } catch (_) { /* print without QR */ }
    }
    // FBR Digital Invoicing: QR of the IRN (separate block on the print).
    if (target?.diIrn) {
      try { enriched.diQr = await QRCode.toDataURL(String(target.diIrn), { margin: 1, width: 160 }); } catch (_) { /* print without QR */ }
    }
    printDocument('saleInvoice', enriched, activeCompany, printConfig, activeBranch, letterhead ? { letterhead } : undefined);
  };

  // Keyboard shortcuts
  useDocumentShortcuts({
    onNew: canEdit ? handleCreate : undefined,
    onSave: canEdit && canPost ? () => handleSave('Posted') : (canEdit ? () => handleSave('Draft') : undefined),
    viewMode,
    saving
  });

  // ===== RENDER =====

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox>
          <FiSearch />
          <input type="text" placeholder="Search by invoice#, customer, DC#, order#..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
            <option value="Partial">Partial</option>
            <option value="Paid">Paid</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Invoice</TranAddButton>}
        </FilterGroup>
      </ActionBar>

      {loading ? (
        <LoadingMessage>Loading sale invoices...</LoadingMessage>
      ) : filteredInvoices.length === 0 ? (
        <TranEmptyState>
          <FiFileText size={48} />
          <h3>No Sale Invoices</h3>
          <p>{invoices.length === 0 ? 'Create your first sale invoice' : 'No invoices match your filters'}</p>
          {invoices.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Invoice</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer>
          {isModuleEnabled('whatsapp_connect') && selectedIds.size > 0 && (
            <BulkBar>
              <span><strong>{selectedIds.size}</strong> selected</span>
              <BulkBtn onClick={handleBulkWhatsApp} disabled={bulkSending}>
                <FaWhatsapp /> {bulkSending ? `Sending ${bulkProgress.done}/${bulkProgress.total}…` : 'Send selected to WhatsApp'}
              </BulkBtn>
              <BulkClear onClick={() => setSelectedIds(new Set())} disabled={bulkSending}>Clear selection</BulkClear>
            </BulkBar>
          )}
          <SortContext.Provider value={sort.ctxValue}>
          <Table>
            <thead>
              <tr>
                {isModuleEnabled('whatsapp_connect') && (
                  <Th style={{ width: 36, textAlign: 'center' }}>
                    <input type="checkbox" onChange={toggleSelectAll}
                      checked={selectedIds.size > 0 && selectedIds.size === filteredInvoices.filter(i => i.status !== 'Draft' && i.status !== 'Cancelled').length}
                      title="Select all eligible" />
                  </Th>
                )}
                <SortableTh field="docNumber">Invoice #</SortableTh>
                <SortableTh field="docDate">Date</SortableTh>
                {showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}
                <SortableTh field="partyName">Customer</SortableTh>
                <SortableTh field="parentDocNumber">Parent Ref</SortableTh>
                <SortableTh field="totalAmount" style={{ textAlign: 'right' }}>Total</SortableTh>
                <SortableTh field="balanceAmount" style={{ textAlign: 'right' }}>Balance</SortableTh>
                <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
                {fbrEnabled && <Th style={{ textAlign: 'center' }}>FBR Inv #</Th>}
                {diEnabled && <Th style={{ textAlign: 'center' }}>FBR IRN</Th>}
                <Th style={{ textAlign: 'center' }}>Links</Th>
                <Th style={{ textAlign: 'center' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map(inv => (
                <Tr key={inv.id}>
                  {isModuleEnabled('whatsapp_connect') && (
                    <Td style={{ textAlign: 'center' }}>
                      {inv.status !== 'Draft' && inv.status !== 'Cancelled' && (
                        <input type="checkbox" checked={selectedIds.has(inv.id)} onChange={() => toggleSelect(inv.id)} />
                      )}
                    </Td>
                  )}
                  <Td><strong>{inv.docNumber}</strong></Td>
                  <Td>{inv.docDate}</Td>
                  {showBranchCol && <Td>{inv.branchName || '-'}</Td>}
                  <Td>{inv.partyName}</Td>
                  <Td>
                    <LinkedDocNumbers {...parentLinks(inv)} showIcon={false} fallback={<span style={{ color: colors.text.tertiary, fontStyle: 'italic' }}>Direct</span>} />
                  </Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500 }}>
                    {activeCompany?.currencyCode || 'PKR'} {formatAmount(inv.totalAmount)}
                  </Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500, color: (inv.balanceAmount || 0) > 0 ? colors.error.main : colors.success.main }}>
                    {formatAmount(inv.balanceAmount ?? inv.totalAmount)}
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <StatusBadge $color={STATUS_COLORS[inv.status]}>{inv.status}</StatusBadge>
                  </Td>
                  {fbrEnabled && (
                    <Td style={{ textAlign: 'center', fontSize: 12 }}>
                      {inv.fbrInvoiceNumber
                        ? <span style={{ color: '#1a7a3d', fontWeight: 600 }} title="FBR fiscal invoice number">{inv.fbrInvoiceNumber}</span>
                        : <span style={{ color: '#bbb' }}>—</span>}
                    </Td>
                  )}
                  {diEnabled && (
                    <Td style={{ textAlign: 'center', fontSize: 12 }}>
                      {inv.diIrn
                        ? <span style={{ color: '#1a7a3d', fontWeight: 600 }} title="FBR Digital Invoicing IRN">{inv.diIrn}</span>
                        : <span style={{ color: '#bbb' }}>—</span>}
                    </Td>
                  )}
                  <Td style={{ textAlign: 'center' }}>
                    <LinkedBadge label={inv.parentTranType || 'From'} {...parentLinks(inv)} />
                    <LinkedBadge label="SR" ids={inv.childIds} />
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <ActionGroup>
                      <IconBtn title="View" onClick={() => handleView(inv)}><FiEye /></IconBtn>
                      {canChange && inv.status !== 'Cancelled' && !inv.fbrInvoiceNumber && !inv.diIrn && <IconBtn title="Edit" onClick={() => handleEdit(inv)}><FiEdit2 /></IconBtn>}
                      <IconBtn title="Print" onClick={() => handlePrint(inv)}><FiPrinter /></IconBtn>
                      {inv.status !== 'Draft' && inv.status !== 'Cancelled' && (
                        <SendWhatsAppButton
                          templateKey="saleInvoice"
                          doc={inv}
                          party={customers.find(c => c.id === inv.partyId)}
                          variant="icon"
                        />
                      )}
                      {canDelete && inv.status !== 'Cancelled' && <IconBtn title="Delete" $danger onClick={() => handleDelete(inv)}><FiTrash2 /></IconBtn>}
                      {canCancel && canEdit && canPost && inv.status !== 'Paid' && inv.status !== 'Cancelled' && (
                        <IconBtn title="Cancel" $danger onClick={() => handleCancel(inv)}><FiXCircle /></IconBtn>
                      )}
                    </ActionGroup>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          </SortContext.Provider>
        </TableContainer>
      )}

      <DocListPagination {...pagination} />

      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredInvoices.length : pagination.totalCount}</strong> invoice(s)</span>
        <span>
          Outstanding: <strong>{formatAmount(listSummary.outstanding)}</strong> |{' '}
          Draft: <strong>{listSummary.counts.Draft || 0}</strong> |{' '}
          Posted: <strong>{listSummary.counts.Posted || 0}</strong> |{' '}
          Paid: <strong>{listSummary.counts.Paid || 0}</strong>
        </span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingInvoice) return null;
    const inv = viewingInvoice;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingInvoice(null); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>
            {inv.docNumber}
            <StatusBadge $color={STATUS_COLORS[inv.status]} style={{ marginLeft: 12 }}>{inv.status}</StatusBadge>
          </FormTitle>
          <FormActions>
            {canChange && inv.status !== 'Cancelled' && !inv.fbrInvoiceNumber && !inv.diIrn && <SaveButton onClick={() => handleEdit(inv)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(inv)}><FiPrinter /> Print</SaveButton>
            {inv.status !== 'Draft' && inv.status !== 'Cancelled' && (
              <SendWhatsAppButton
                templateKey="saleInvoice"
                doc={inv}
                party={customers.find(c => c.id === inv.partyId)}
                variant="button"
              />
            )}
            {fbrEnabled && inv.status !== 'Draft' && inv.status !== 'Cancelled' && inv.reportToFbr !== false && (
              inv.fbrInvoiceNumber
                ? <SaveButton $secondary disabled title={'FBR Invoice #: ' + inv.fbrInvoiceNumber} style={{ background: '#e7f6ec', color: '#1a7a3d', borderColor: '#b7e2c4', cursor: 'default' }}><FiCheck /> FBR&nbsp;#{inv.fbrInvoiceNumber}</SaveButton>
                : <SaveButton $approve onClick={() => handleSendToFbr(inv)} disabled={fbrSending}>{fbrSending ? 'Sending…' : 'Send to FBR'}</SaveButton>
            )}
            {diEnabled && inv.status !== 'Draft' && inv.status !== 'Cancelled' && inv.reportToFbr !== false && (
              inv.diIrn
                ? <SaveButton $secondary disabled title={'FBR IRN: ' + inv.diIrn} style={{ background: '#e7f6ec', color: '#1a7a3d', borderColor: '#b7e2c4', cursor: 'default' }}><FiCheck /> FBR&nbsp;DI&nbsp;✓</SaveButton>
                : <SaveButton $approve onClick={() => handleSendToDi(inv)} disabled={diSending}>{diSending ? 'Sending…' : 'Send to FBR (Digital Invoice)'}</SaveButton>
            )}
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Invoice Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Invoice Number</label><span>{inv.docNumber}</span></InfoItem>
            <InfoItem><label>Invoice Date</label><span>{inv.docDate}</span></InfoItem>
            <InfoItem><label>Customer</label><span>{inv.partyName}</span></InfoItem>
            <InfoItem>
              <label>Parent Reference</label>
              <span>
                <LinkedDocNumbers {...parentLinks(inv)} max={0} showIcon={false} fallback={<span style={{ color: colors.text.tertiary, fontStyle: 'italic' }}>Direct Invoice</span>} />
              </span>
            </InfoItem>
            <InfoItem><label>Customer PO #</label><span>{inv.customerPO || '-'}</span></InfoItem>
            <InfoItem><label>Due Date</label><span>{inv.dueDate || '-'}</span></InfoItem>
            <InfoItem><label>Payment Terms</label><span>{inv.paymentTerms || '-'}</span></InfoItem>
            <InfoItem><label>Currency</label><span>{activeCompany?.currencyCode || 'PKR'}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{inv.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{inv.jobName || '-'}</span></InfoItem>
            <InfoItem><label>Location</label><span>{inv.locationName || '-'}</span></InfoItem>
          </InfoGrid>
          {(inv.parentId || inv.childIds?.length > 0) && (
            <LinkedDocsSection>
              <LinkedBadge label={`Linked ${inv.parentTranType || 'Doc'}`} {...parentLinks(inv)} max={0} />
              <LinkedBadge label="Linked SR" ids={inv.childIds} max={0} />
            </LinkedDocsSection>
          )}
        </FormCard>

        <FormCard>
          <SectionTitle>Line Items</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th><Th style={{ width: 40 }}></Th><Th>Description</Th>
                  <Th style={{ textAlign: 'right' }}>Qty</Th>
                  <Th style={{ textAlign: 'right' }}>Unit Price</Th>
                  <Th style={{ textAlign: 'right' }}>Disc %</Th>
                  <Th style={{ textAlign: 'right' }}>Tax</Th>
                  <Th style={{ textAlign: 'right' }}>Total</Th>
                </tr>
              </thead>
              <tbody>
                {(inv.lines || []).map((line, i) => {
                  const itm = items.find(it => it.id === line.itemId);
                  return (
                  <Tr key={i}>
                    <Td>{i + 1}</Td>
                    <Td style={{ padding: '2px 4px' }}>
                      {itm?.imageUrl ? <ItemThumb src={itm.imageUrl} alt="" /> : <ItemThumbEmpty><FiPackage size={14} /></ItemThumbEmpty>}
                    </Td>
                    <Td>{line.description}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? <span style={{ fontSize: '11px', color: '#64748B', marginLeft: '4px' }}>{line.unitOfMeasure}</span> : ''}<TwoUnitNote line={line} /></Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitPrice)}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.discountPercent || 0}%</Td>
                    <Td style={{ textAlign: 'right' }}>{line.taxable ? formatAmount(line.taxAmount) : '-'}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.lineTotal)}</Td>
                  </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableContainer>
          <TotalsSection>
            <TotalsTable>
              <tbody>
                <tr><td>Subtotal</td><td>{formatAmount(inv.subtotal)}</td></tr>
                {inv.discountAmount > 0 && <tr><td>Discount</td><td>-{formatAmount(inv.discountAmount)}</td></tr>}
                <tr><td>Tax</td><td>{formatAmount(inv.taxAmount)}</td></tr>
                {(() => {
                  const m = (inv.lines || []).reduce((s, l) => s + (Number(l.mrp) || 0), 0);
                  return m > 0 ? <tr><td>MRP</td><td>{formatAmount(m)}</td></tr> : null;
                })()}
                {inv.whtAmount > 0 && <tr><td>{whtLabel} ({inv.whtRate || 0}%)</td><td>{inv.whtAddToTotal ? '' : '-'}{formatAmount(inv.whtAmount)}</td></tr>}
                {Number(inv.transportationCharges) > 0 && <tr><td>Transportation / Carriage</td><td>{formatAmount(inv.transportationCharges)}</td></tr>}
                {Number(inv.otherCharges) > 0 && <tr><td>Other Charges</td><td>{formatAmount(inv.otherCharges)}</td></tr>}
                <TotalRow><td>Total ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(inv.totalAmount)}</td></TotalRow>
                <tr><td>Paid</td><td style={{ color: colors.success.main }}>{formatAmount(inv.paidAmount || 0)}</td></tr>
                <BalanceRow><td>Balance Due</td><td>{formatAmount(inv.balanceAmount ?? inv.totalAmount)}</td></BalanceRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>
        </FormCard>

        <AttachmentPanel
          transactionId={inv?.id}
          attachment={inv?.attachment}
          canEdit={false}
        />

        {inv.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{inv.notes}</NotesText></FormCard>}
      </>
    );
  };

  const renderFormView = () => {
    // Linkable DCs = the open (Delivered/Dispatched) DCs for this customer. A DC
    // leaves this list the moment it is invoiced (its status flips to 'Invoiced'),
    // so no cross-invoice scan is needed to exclude already-invoiced ones.
    // Only this branch's challans / orders (see utils/branchLink.js).
    const customerDCs = deliveredDCs
      .filter(d => !formData.partyId || d.partyId === formData.partyId)
      .filter(d => inActiveBranch(d, activeBranch));
    const customerSOs = approvedSOs.filter(s => !formData.partyId || s.partyId === formData.partyId)
      .filter(s => inActiveBranch(s, activeBranch, formData.parentTranType === 'SO' ? formData.parentId : null));
    const isLinked = !!formData.parentId;
    const isMultiDC = formData.parentTranType === 'DC' && (formData.parentIds?.length || 0) > 0;
    const linkedDCObjects = (formData.parentIds || []).map(id => deliveredDCs.find(d => d.id === id)).filter(Boolean);
    const availableDCs = customerDCs.filter(d => !formData.parentIds?.includes(d.id));
    const hasSourcedLines = lineItems.some(l => !!l.sourceDocId);
    // FLW-03: Ordered / Already Delivered / Remaining columns for an invoice made from a Sale Order.
    const soCols = formData.parentTranType === 'SO' && !!formData.parentId && !!soProgress;

    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); resetForm(); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>{editingInvoice ? `Edit: ${editingInvoice.docNumber}` : 'New Sale Invoice'}</FormTitle>
          <FormActions>
            <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}>
              <FiX /> Cancel
            </SaveButton>
            {canEdit && !(editingInvoice && editingInvoice.status && editingInvoice.status !== 'Draft' && editingInvoice.status !== 'Cancelled') && (
              <SaveButton onClick={() => handleSave('Draft')} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save Draft'}
              </SaveButton>
            )}
            {canEdit && canPost && (formData.status === 'Draft' || !editingInvoice) && (
              <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}>
                <FiCheck /> Post Invoice
              </SaveButton>
            )}
            {/* ACC-05: editing a posted document keeps it posted (no silent drop to Draft) */}
            {canEdit && (editingInvoice && editingInvoice.status && editingInvoice.status !== 'Draft' && editingInvoice.status !== 'Cancelled') && (
              <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> {saving ? 'Saving...' : 'Save Changes'}</SaveButton>
            )}
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Invoice Details</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Invoice Number</Label>
              <Input value={editingInvoice?.docNumber || 'Auto-generated'} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Invoice Date *</Label>
              <Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Input value={formData.status} disabled />
            </FormGroup>
            <FormGroup $span2>
              <Label>Link to Delivery Challan(s) {isMultiDC ? `(${linkedDCObjects.length})` : ''}</Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {linkedDCObjects.map(d => (
                  <span key={d.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: colors.primary.main + '15', color: colors.primary.main,
                    border: `1px solid ${colors.primary.main}40`,
                    padding: '4px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500
                  }}>
                    {d.docNumber}
                    <FiX size={12} style={{ cursor: 'pointer' }} onClick={() => removeDC(d.id)} title="Remove this DC" />
                  </span>
                ))}
                <Select
                  value=""
                  onChange={e => { addDC(e.target.value); e.target.value = ''; }}
                  disabled={formData.parentTranType === 'SO'}
                  style={{ minWidth: 160, flex: linkedDCObjects.length === 0 ? 1 : '0 0 auto' }}
                >
                  <option value="">{linkedDCObjects.length === 0 ? '-- Direct Invoice (No DC) --' : '+ Add another DC'}</option>
                  {availableDCs.map(d => (
                    <option key={d.id} value={d.id}>{d.docNumber} - {d.partyName} ({d.docDate || ''})</option>
                  ))}
                </Select>
              </div>
            </FormGroup>
            <FormGroup>
              <Label>Link to Sale Order</Label>
              <Select value={formData.parentTranType === 'SO' ? formData.parentId : ''} onChange={e => handleSOChange(e.target.value)} disabled={isMultiDC}>
                <option value="">-- No Sale Order --</option>
                {customerSOs.map(s => (
                  <option key={s.id} value={s.id}>{s.docNumber} - {s.partyName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup $span2>
              <Label>Customer *</Label>
              <CustomerSelect customers={customers} selectedCustomerId={formData.partyId} onChange={handleCustomerChange} disabled={isLinked || isMultiDC} balanceOnDemand />
            </FormGroup>
            {customers.find(c => c.id === formData.partyId)?.isWalkIn && (
              <>
                <FormGroup>
                  <Label>Walk-in Customer Name (optional)</Label>
                  <Input value={formData.walkInName} onChange={e => handleHeaderChange('walkInName', e.target.value)}
                    placeholder="Name for this invoice only" disabled={isLinked || isMultiDC} />
                </FormGroup>
                <FormGroup>
                  <Label>Walk-in Phone (optional)</Label>
                  <Input value={formData.walkInPhone} onChange={e => handleHeaderChange('walkInPhone', e.target.value)}
                    placeholder="Phone for this invoice only" disabled={isLinked || isMultiDC} />
                </FormGroup>
              </>
            )}
            <FormGroup>
              <Label>Due Date</Label>
              <Input type="date" value={formData.dueDate} onChange={e => handleHeaderChange('dueDate', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Payment Terms</Label>
              <Input value={formData.paymentTerms} onChange={e => handleHeaderChange('paymentTerms', e.target.value)} placeholder="e.g., 30 days" />
            </FormGroup>
            <FormGroup>
              <Label>Customer PO # (optional)</Label>
              <Input value={formData.customerPO} onChange={e => handleHeaderChange('customerPO', e.target.value)} placeholder="Customer's PO number" />
            </FormGroup>
            <FormGroup>
              <Label>Parent Reference</Label>
              <Input value={formData.parentDocNumber} disabled placeholder="From linked document" />
            </FormGroup>
            <FormGroup>
              <Label>Currency</Label>
              <Input value={activeCompany?.currencyCode || 'PKR'} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Employee</Label>
              <Select value={formData.employeeId} onChange={e => {
                const emp = employees.find(em => em.id === e.target.value);
                handleHeaderChange('employeeId', e.target.value);
                handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
              }}>
                <option value="">-- Select Employee --</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Job</Label>
              <Select value={formData.jobId} onChange={e => {
                const job = jobs.find(j => j.id === e.target.value);
                handleHeaderChange('jobId', e.target.value);
                handleHeaderChange('jobName', job?.jobName || '');
              }}>
                <option value="">-- Select Job --</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>{reqLabel(activeCompany, 'Location')}</Label>
              <Select value={formData.locationId} onChange={e => {
                const loc = locations.find(l => l.id === e.target.value);
                handleHeaderChange('locationId', e.target.value);
                handleHeaderChange('locationName', loc?.locationName || '');
              }} disabled={!!formData.dcId}>
                <option value="">-- Select Location --</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.locationName}</option>
                ))}
              </Select>
            </FormGroup>
          </FormGrid>
        </FormCard>

        <FormCard>
          <LineItemsHeader>
            <SectionTitle style={{ margin: 0 }}>Line Items</SectionTitle>
            {!isLinked && !isMultiDC && <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>}
          </LineItemsHeader>

          <LineTableContainer>
            <LineTable>
              <thead>
                <tr>
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
                  {hasSourcedLines && <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>DC #</LineTh>}
                  {hasSourcedLines && <LineTh style={{ width: 110, minWidth: 110, maxWidth: 110 }}>DC Date</LineTh>}
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
                  <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh>
                  <LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Description</LineTh>
                  {lineItems.some(l => l.hsCode) && <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>HS Code</LineTh>}
                  {soCols && <LineTh style={{ width: 110, minWidth: 110, maxWidth: 110 }}>Ordered</LineTh>}
                  {soCols && <LineTh style={{ width: 110, minWidth: 110, maxWidth: 110 }}>Already Delivered</LineTh>}
                  {soCols && <LineTh style={{ width: 110, minWidth: 110, maxWidth: 110 }}>Remaining</LineTh>}
                  <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh>
                  <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Price Type</LineTh>
                  <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Price</LineTh>
                  <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Disc % / Amt</LineTh>
                  <LineTh style={{ width: 80, minWidth: 80, maxWidth: 80 }}>{activeCompany?.taxLabel || 'GST'}?</LineTh>
                  <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Tax %</LineTh>
                  {mrpEnabled && <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>MRP</LineTh>}
                  <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
                  {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 160, minWidth: 160, maxWidth: 160 }}>Batch</LineTh>}
                  {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Serial Numbers</LineTh>}
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((line, index) => {
                  const calc = calculateLine(line);
                  const isSourced = !!line.sourceDocId;
                  const soP = soCols ? soProgress[line.soLineNumber] : null;
                  const soRemaining = soP ? Math.max(0, r6(soP.ordered - soP.delivered)) : null;
                  const soOver = soRemaining !== null && (Number(line.quantity) || 0) > soRemaining + 1e-6;
                  return (
                    <React.Fragment key={index}><tr>
                      <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                      {hasSourcedLines && <LineTd style={{ fontSize: 11, color: colors.text.secondary }}>{line.sourceDocNumber || '-'}</LineTd>}
                      {hasSourcedLines && <LineTd style={{ fontSize: 11, color: colors.text.secondary }}>{line.sourceDocDate || '-'}</LineTd>}
                      <LineTd style={{ padding: '2px 4px' }}>
                        {(() => { const itm = items.find(i => i.id === line.itemId); return itm?.imageUrl ? (
                          <ItemThumb src={itm.imageUrl} alt="" />
                        ) : line.itemId ? (
                          <ItemThumbEmpty><FiPackage size={14} /></ItemThumbEmpty>
                        ) : null; })()}
                      </LineTd>
                      <LineTd>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <ItemSelect
                              ref={(el) => { lineItemRefs.current[index] = el; }}
                              items={filterItemsByPricing(items, customerPricing)}
                              selectedItemId={line.itemId}
                              onChange={(itemId) => handleLineChange(index, 'itemId', itemId)}
                              disabled={isLinked || isSourced}
                              stockBalances={stockBalances}
                              stockOnDemand
                              onRequestStock={requestItemStock}
                            />
                          </div>
                          {!(isLinked || isSourced) && (
                            <button type="button" title="Quick add new item"
                              onClick={() => { setQuickAddIndex(index); setShowQuickAdd(true); }}
                              style={{ flexShrink: 0, width: 26, height: 26, border: '1px solid #ccc', borderRadius: 6, background: '#fff', color: '#800020', fontSize: 16, fontWeight: 700, cursor: 'pointer', lineHeight: 1 }}>+</button>
                          )}
                        </div>
                        {line.itemId && <ItemPriceHints hints={priceRefs[line.itemId]} />}
                      </LineTd>
                      <LineTd>
                        <LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)}
                          placeholder="Description" disabled={isLinked || isSourced} />
                        
                      </LineTd>
                      {lineItems.some(l => l.hsCode) && (
                        <LineTd>
                          <LineInput value={line.hsCode || ''} onChange={e => handleLineChange(index, 'hsCode', e.target.value)}
                            placeholder="HS Code" disabled={isLinked || isSourced} />
                        </LineTd>
                      )}
                      {soCols && <LineTd><LineInput type="number" value={soP ? soP.ordered : ''} placeholder="-" disabled style={{ textAlign: 'right', background: colors.neutral[100] }} /></LineTd>}
                      {soCols && <LineTd><LineInput type="number" value={soP ? soP.delivered : ''} placeholder="-" disabled style={{ textAlign: 'right', background: colors.neutral[100] }} /></LineTd>}
                      {soCols && <LineTd><LineInput type="number" value={soRemaining === null ? '' : soRemaining} placeholder="-" disabled style={{ textAlign: 'right', background: colors.neutral[100] }} /></LineTd>}
                      <LineTd>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <LineInput type="number" min="0" value={line.quantity}
                            onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))}
                            disabled={isSourced || line.hasSize}
                            title={line.hasSize ? 'Auto-computed from L × H × Pieces' : undefined}
                            style={{ textAlign: 'right', background: line.hasSize ? '#F1F5F9' : undefined, color: soOver ? colors.error.main : undefined }} />
                          {(() => {
                            const itm = items.find(i => i.id === line.itemId);
                            const itmUnits = itm && Array.isArray(itm.units) ? itm.units : [];
                            if (multiUnitEnabled && itmUnits.length > 1) {
                              return (
                                <Select
                                  value={line.unitCode || line.unitOfMeasure || ''}
                                  onChange={e => handleLineChange(index, 'unitCode', e.target.value)}
                                  disabled={isLinked}
                                  style={{ padding: '2px 4px', fontSize: '11px', minWidth: 56 }}
                                >
                                  {itmUnits.map(u => (
                                    <option key={u.unitCode} value={u.unitCode}>{u.unitCode}</option>
                                  ))}
                                </Select>
                              );
                            }
                            return line.unitOfMeasure ? (
                              <span style={{ fontSize: '11px', color: '#64748B', whiteSpace: 'nowrap' }}>{line.unitOfMeasure}</span>
                            ) : null;
                          })()}
                        </div>
                        <TwoUnitQtyBox item={items.find(i => i.id === line.itemId)} unitCode={line.unitCode || line.unitOfMeasure}
                          quantity={line.quantity} hasSize={line.hasSize} disabled={isSourced}
                          onChange={(q) => handleLineChange(index, 'quantity', q)} />
                      </LineTd>
                      <LineTd>
                        <Select value={line.priceType || 'Retail'} onChange={e => handleLineChange(index, 'priceType', e.target.value)} style={{ padding: '4px 6px', fontSize: '12px', minWidth: 80 }}>
                          <option value="Retail">Retail</option>
                          <option value="Wholesale">Wholesale</option>
                          <option value="Dealer">Dealer</option>
                        </Select>
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" min="0" step="0.01" value={line.unitPrice}
                          onChange={e => handleLineChange(index, 'unitPrice', Number(e.target.value))}
                          onBlur={() => handlePriceBlur(index)} style={{ textAlign: 'right' }} />
                      </LineTd>
                      <LineTd>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <LineInput type="number" min="0" max="100" step="0.01" value={line.discountPercent}
                            onChange={e => handleLineChange(index, 'discountPercent', Number(e.target.value))} style={{ textAlign: 'right' }} title="Discount %" placeholder="%" />
                          <LineInput type="number" min="0" step="0.01" value={calc.discountAmount || 0}
                            onChange={e => handleLineChange(index, 'discountAmountInput', Number(e.target.value))} style={{ textAlign: 'right' }} title="Discount amount" placeholder="Amt" />
                        </div>
                      </LineTd>
                      <LineTd style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={line.taxable}
                          onChange={e => handleLineChange(index, 'taxable', e.target.checked)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !line.taxable) handleLastFieldEnter(e, index); }}
                        />
                      </LineTd>
                      <LineTd>
                        <LineInput
                          type="number" min="0" max="100" value={line.taxRate}
                          onChange={e => handleLineChange(index, 'taxRate', Number(e.target.value))}
                          onKeyDown={(e) => handleLastFieldEnter(e, index)}
                          disabled={!line.taxable}
                          style={{ textAlign: 'right', opacity: line.taxable ? 1 : 0.4 }}
                        />
                      </LineTd>
                      {mrpEnabled && (
                        <LineTd>
                          <LineInput type="number" min="0" step="0.01" value={line.mrp || 0}
                            onChange={e => handleLineChange(index, 'mrp', Number(e.target.value))}
                            style={{ textAlign: 'right' }} />
                        </LineTd>
                      )}
                      <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                      {lineItems.some(l => l.trackingType === 'Batch') && (
                        <LineTd>{line.trackingType === 'Batch' ? (
                          <Select value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} style={{ padding: '6px 8px', fontSize: '13px', minWidth: 120 }}>
                            <option value="">Select Batch</option>
                            {(availableBatches[line.itemId] || []).map(b => (
                              <option key={b.batchNumber} value={b.batchNumber}>{b.batchNumber} (Qty: {b.remaining}{b.expiryDate ? `, Exp: ${b.expiryDate}` : ''})</option>
                            ))}
                          </Select>
                        ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
                      )}
                      {lineItems.some(l => l.trackingType === 'Serial') && (
                        <LineTd>{line.trackingType === 'Serial' ? (
                          <SerialSelectPopover
                            availableSerials={availableSerials[line.itemId] || []}
                            selectedSerials={line.serialNumbers || []}
                            requiredCount={line.quantity || 0}
                            onChange={(serials) => handleLineChange(index, 'serialNumbers', serials)}
                            disabled={!!formData.parentId}
                          />
                        ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
                      )}
                      <LineTd style={{ textAlign: 'center' }}>
                        {!isLinked && lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)} title="Remove"><FiX /></RemoveLineBtn>}
                      </LineTd>
                    </tr>
                    {line.hasSize && (<SizeLineRow line={line} index={index} onChange={handleLineChange} disabled={isLinked || isSourced} leftColSpan={3 + (hasSourcedLines ? 2 : 0)} />)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </LineTable>
          </LineTableContainer>

          <TotalsSection>
            <TotalsTable>
              <tbody>
                <tr><td>Subtotal</td><td>{formatAmount(totals.subtotal)}</td></tr>
                <tr>
                  <td>Discount</td>
                  <td><DiscountInput type="number" min="0" step="0.01" value={formData.discountAmount} onChange={e => handleHeaderChange('discountAmount', Number(e.target.value))} /></td>
                </tr>
                <tr><td>Tax</td><td>{formatAmount(totals.taxAmount)}</td></tr>
                {mrpEnabled && totals.mrpTotal > 0 && (
                  <tr><td>MRP</td><td>{formatAmount(totals.mrpTotal)}</td></tr>
                )}
                <tr>
                  <td>{whtLabel} %</td>
                  <td><DiscountInput type="number" min="0" max="100" step="0.5" value={formData.whtRate} onChange={e => handleHeaderChange('whtRate', Number(e.target.value))} /></td>
                </tr>
                {totals.whtAmount > 0 && <tr><td>{whtLabel} Amount</td><td>{whtAddToTotal ? '' : '-'}{formatAmount(totals.whtAmount)}</td></tr>}
                <tr>
                  <td>Transportation / Carriage</td>
                  <td><DiscountInput type="number" min="0" step="0.01" value={formData.transportationCharges} onChange={e => handleHeaderChange('transportationCharges', Number(e.target.value))} /></td>
                </tr>
                <tr>
                  <td>Other Charges</td>
                  <td><DiscountInput type="number" min="0" step="0.01" value={formData.otherCharges} onChange={e => handleHeaderChange('otherCharges', Number(e.target.value))} /></td>
                </tr>
                <TotalRow><td>Total ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalAmount)}</td></TotalRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>

          <PaymentSection>
            <SectionTitle>Payment</SectionTitle>
            <FormGrid>
              {formData.existingPaidAmount > 0 && (
                <FormGroup>
                  <Label>Received via Vouchers</Label>
                  <Input value={formatAmount(formData.existingPaidAmount)} disabled
                    style={{ fontWeight: 600, color: colors.success.main }} />
                </FormGroup>
              )}
              <FormGroup>
                <Label>Amount Received</Label>
                <Input type="number" min="0" step="0.01" value={formData.paymentAmount}
                  onChange={e => handleHeaderChange('paymentAmount', Math.min(Number(e.target.value), totals.totalAmount - formData.existingPaidAmount))}
                  placeholder="0.00" />
              </FormGroup>
              <FormGroup>
                <Label>Payment Account</Label>
                <Select value={formData.paymentAccountId} onChange={e => {
                  const acc = paymentAccounts.find(a => a.id === e.target.value);
                  handleHeaderChange('paymentAccountId', e.target.value);
                  handleHeaderChange('paymentAccountName', acc?.name || '');
                }}>
                  <option value="">-- Select Account --</option>
                  <optgroup label="Cash Accounts">
                    {paymentAccounts.filter(a => a.code?.startsWith('11101')).map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Bank Accounts">
                    {paymentAccounts.filter(a => a.code?.startsWith('11102')).map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Credit Account (override)</Label>
                <Select value={formData.creditAccountId} onChange={e => {
                  const acc = allAccounts.find(a => a.id === e.target.value);
                  handleHeaderChange('creditAccountId', e.target.value);
                  handleHeaderChange('creditAccountCode', acc?.code || '');
                  handleHeaderChange('creditAccountName', acc?.name || '');
                }}>
                  <option value="">-- Default: Sales – General --</option>
                  {allAccounts.map(a => (
                    <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                  ))}
                </Select>
              </FormGroup>
              {(formData.existingPaidAmount > 0 || Number(formData.paymentAmount) > 0) && (
                <FormGroup>
                  <Label>Balance After Receipt</Label>
                  <Input value={formatAmount(totals.totalAmount - formData.existingPaidAmount - (Number(formData.paymentAmount) || 0))} disabled
                    style={{ fontWeight: 600, color: colors.error.main }} />
                </FormGroup>
              )}
            </FormGrid>
          </PaymentSection>
        </FormCard>

        <FormCard>
          <SectionTitle>Additional Information</SectionTitle>
          <FormGrid>
            <FormGroup $span2>
              <Label>Notes</Label>
              <TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Internal notes..." rows={3} />
            </FormGroup>
            {fbrEnabled && (
              <FormGroup $span2>
                <Label>FBR POS</Label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '6px 0' }}>
                  <input type="checkbox" checked={formData.reportToFbr !== false} onChange={e => handleHeaderChange('reportToFbr', e.target.checked)} />
                  Report this invoice to FBR (enables the "Send to FBR" button after posting)
                </label>
              </FormGroup>
            )}
          </FormGrid>
        </FormCard>

        <AttachmentPanel
          transactionId={editingInvoice?.id}
          attachment={editingInvoice?.attachment}
          canEdit={canEdit}
          onChange={({ attachment }) => setEditingInvoice(prev => prev ? { ...prev, attachment } : prev)}
        />
      </>
    );
  };

  return (
    <PageLayout pageTitle="Sale Invoice" breadcrumb="Sales / Sale Invoice">
      {letterheadPickFor && (
        <LetterheadPicker
          companyId={activeCompany?.id}
          companyName={activeCompany?.companyName}
          letterheads={listInvoiceLetterheads(printConfig)}
          onClose={() => setLetterheadPickFor(null)}
          onPrint={(choice) => {
            const pick = letterheadPickFor;
            setLetterheadPickFor(null);
            handlePrint(pick.inv, letterheadForChoice(choice, listInvoiceLetterheads(printConfig)));
          }}
        />
      )}
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
      {showQuickAdd && (
        <QuickAddItemModal
          companyId={activeCompany?.id}
          existingItems={items}
          onCreated={handleQuickItemCreated}
          onClose={() => { setShowQuickAdd(false); setQuickAddIndex(null); }}
        />
      )}
    </PageLayout>
  );
};

const BulkBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  background: #e8f5e9; border: 1px solid #a5d6a7;
  padding: 10px 16px; border-radius: 8px; margin-bottom: 10px;
  font-size: 14px;
`;
const BulkBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  background: #25D366; color: #fff; border: none;
  padding: 8px 14px; border-radius: 6px; font-weight: 600; cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const BulkClear = styled.button`
  background: transparent; border: 1px solid #1b5e20; color: #1b5e20;
  padding: 6px 12px; border-radius: 6px; cursor: pointer; font-size: 13px;
`;

const ItemThumb = styled.img`
  width: 32px;
  height: 32px;
  border-radius: ${borderRadius.sm};
  object-fit: cover;
  border: 1px solid ${colors.border.light};
`;

const ItemThumbEmpty = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${borderRadius.sm};
  border: 1px dashed ${colors.border.main};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.secondary};
  opacity: 0.4;
`;

export default SaleInvoice;
